// ============================================================
//  src/pages/bots/tabs/FlowsTab.js
//  Updated: Real Meta health check + Sign Public Key flow
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { copyToClipboard, formatDate } from '../../../utils/helpers';
import './tabs.css';
import './FlowsTab.css';

// Coerces a value that's SUPPOSED to be an array (e.g. a component's
// "data-source") into one safely. Malformed flow JSON that's technically
// still valid JSON -- e.g. "data-source": "oops" instead of an array --
// used to reach a bare .map() call and throw mid-render, which (with no
// Error Boundary anywhere in this tree) unmounted the entire page. This
// is the actual fix for that specific crash; ErrorBoundary below is the
// safety net for anything else along the same lines.
function asArray(val) {
  return Array.isArray(val) ? val : [];
}

// React only stops an uncaught render error from unmounting the whole
// tree if something implements componentDidCatch/getDerivedStateFromError
// -- there was nothing here before, so ANY unexpected shape in a user's
// pasted flow.json (not just the data-source case above) could blank the
// entire page with no way back except reloading and hoping unsaved edits
// survived. This scopes that blast radius to just the panel it wraps.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[FlowsTab] Preview render error:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flow-preview-crash">
          <div style={{ fontSize: 28 }}>⚠️</div>
          <div style={{ fontWeight: 700, marginTop: 6 }}>Couldn't render this preview</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, maxWidth: 280 }}>
            Something in the flow JSON has an unexpected shape (e.g. a field that should be a list isn't one).
            Your editor content is safe — check the <strong>Flow JSON Errors</strong> tab, or open the browser console for the exact error.
          </div>
          <button
            className="btn btn-outline"
            style={{ marginTop: 12, fontSize: 12.5 }}
            onClick={() => this.setState({ error: null })}
          >
            Try rendering again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function flowsApi(bId, body) {
  const token = localStorage.getItem('sAuthToken');
  const res = await fetch(`${API_BASE}/bots/flows/?bId=${encodeURIComponent(bId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  return res.json();
}

async function flowsGet(bId) {
  const token = localStorage.getItem('sAuthToken');
  const res = await fetch(`${API_BASE}/bots/flows/?bId=${encodeURIComponent(bId)}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res.json();
}

const CATEGORIES = [
  'OTHER', 'SIGN_UP', 'SIGN_IN', 'APPOINTMENT_BOOKING',
  'LEAD_GENERATION', 'CONTACT_US', 'CUSTOMER_SUPPORT', 'SURVEY', 'SHOPPING',
];

const STATUS_STYLE = {
  draft:       { bg: '#f3f4f6', color: '#374151' },
  published:   { bg: '#dcfce7', color: '#15803d' },
  deprecated:  { bg: '#fee2e2', color: '#b91c1c' },
};

// Components that require certain fields. Keys are lowercased type names.
const COMPONENT_RULES = {
  textinput:        { required: ['name', 'label'] },
  'text-input':     { required: ['name', 'label'] },
  textarea:         { required: ['name', 'label'] },
  radiobuttons:     { required: ['name', 'data-source'] },
  'radio-buttons':  { required: ['name', 'data-source'] },
  checkboxes:       { required: ['name', 'data-source'] },
  'checkbox-group': { required: ['name', 'data-source'] },
  dropdown:         { required: ['name', 'data-source'] },
  datepicker:       { required: ['name'] },
  'date-picker':    { required: ['name'] },
  image:            { required: ['src'] },
};

// Every component "type" value this file already knows how to render,
// UNIONED with real Meta WhatsApp Flow component types this preview
// doesn't render yet (RichText, CalendarPicker, OptIn, etc.) -- those
// aren't errors, just not drawn specially (they fall through to a
// generic placeholder box). This set exists purely to catch genuinely
// invalid values -- typos, made-up names -- the same class of mistake
// Meta's own validator flags as "Invalid value found for property
// 'type'." Kept deliberately permissive (both hyphenated and
// non-hyphenated spellings) to match this file's own existing tolerance
// rather than introduce new false positives.
const VALID_COMPONENT_TYPES = new Set([
  'textheading', 'text-heading',
  'textsubheading', 'text-subheading',
  'textbody', 'text-body', 'text',
  'textcaption', 'text-caption',
  'richtext', 'rich-text',
  'textinput', 'text-input',
  'textarea',
  'checkboxgroup', 'checkbox-group', 'checkboxes',
  'radiobuttonsgroup', 'radio-buttons-group', 'radiobuttons', 'radio-buttons',
  'dropdown',
  'datepicker', 'date-picker',
  'calendarpicker', 'calendar-picker',
  'optin', 'opt-in',
  'chipsselector', 'chips-selector',
  'image',
  'imagecarousel', 'image-carousel',
  'photopicker', 'photo-picker',
  'documentpicker', 'document-picker',
  'footer',
  'embeddedlink', 'embedded-link',
  'navigationlist', 'navigation-list',
  'form', 'if', 'switch',
]);

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n];
}

// Best-effort "did you mean X?" for a misspelled component type --
// Meta's own error doesn't offer this, but it's cheap and genuinely
// useful given typos (e.g. "TextInptu") are the single most common
// version of this exact error.
const CANONICAL_TYPE_NAMES = [
  'TextHeading', 'TextSubheading', 'TextBody', 'TextCaption', 'RichText',
  'TextInput', 'TextArea', 'CheckboxGroup', 'RadioButtonsGroup', 'Dropdown',
  'DatePicker', 'CalendarPicker', 'OptIn', 'ChipsSelector', 'Image',
  'ImageCarousel', 'PhotoPicker', 'DocumentPicker', 'Footer', 'EmbeddedLink',
  'NavigationList', 'Form', 'If', 'Switch',
];
// Properties allowed per component type, for flagging things like
// "Property 'color' is not allowed in 'TextBody' component." -- kept
// deliberately scoped to the text/display components (plus a few simple
// structural ones) where the real Meta schema is small and stable
// enough to be confident about. Input/selection components have larger,
// more varied property sets; getting one wrong there risks a false
// positive that's worse than not checking at all, so this intentionally
// does not attempt to cover every component type.
const ALLOWED_PROPS = {
  textheading:     ['type', 'text', 'visible'],
  'text-heading':  ['type', 'text', 'visible'],
  textsubheading:    ['type', 'text', 'visible'],
  'text-subheading': ['type', 'text', 'visible'],
  textbody:    ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  'text-body': ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  text:        ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  textcaption:    ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  'text-caption': ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  richtext:    ['type', 'text', 'visible'],
  'rich-text': ['type', 'text', 'visible'],
  footer: ['type', 'label', 'left-caption', 'center-caption', 'right-caption', 'on-click-action', 'enabled', 'visible'],
  embeddedlink:    ['type', 'text', 'on-click-action', 'visible'],
  'embedded-link': ['type', 'text', 'on-click-action', 'visible'],
  image: ['type', 'src', 'width', 'height', 'scale-type', 'aspect-ratio', 'visible', 'alt-text'],
  // Added after a real Meta validator response confirmed "placeholder"
  // is NOT accepted here (a very easy mistake -- "label" is the field
  // that plays that role in WhatsApp Flow's TextInput, not
  // "placeholder" like most other UI frameworks use).
  textinput:    ['type', 'name', 'label', 'input-type', 'required', 'visible', 'helper-text', 'error-message', 'min-chars', 'max-chars', 'pattern', 'init-value', 'enabled'],
  'text-input': ['type', 'name', 'label', 'input-type', 'required', 'visible', 'helper-text', 'error-message', 'min-chars', 'max-chars', 'pattern', 'init-value', 'enabled'],
};

// Meta's dynamic "visible" only accepts a bare single-field reference --
// ${data.x} or ${screen.data.x} -- NOT a compound expression with
// operators (${data.x == y}, ${data.list.length > 0}, etc.), even
// though those look like reasonable boolean expressions. Anything that
// isn't a literal boolean or this exact shape gets rejected by Meta as
// a plain string, which is what produces its "expected boolean but
// found string" message despite the value visually looking dynamic.
const VISIBLE_DYNAMIC_RE = /^\$\{(screen\.)?data\.[a-zA-Z0-9_]+\}$/;

function suggestComponentType(badType) {
  if (!badType) return null;
  const lower = badType.toLowerCase();
  let best = null, bestDist = Infinity;
  for (const name of CANONICAL_TYPE_NAMES) {
    const d = levenshtein(lower, name.toLowerCase());
    if (d < bestDist) { bestDist = d; best = name; }
  }
  // Only suggest if it's plausibly a typo, not a totally unrelated word
  return (best && bestDist <= Math.max(3, Math.ceil(badType.length * 0.4))) ? best : null;
}

// ── Position-tracking JSON parser ───────────────────────────────
//  A minimal hand-rolled JSON parser that records {line, column} for
//  the start of every key and value it parses, alongside the normal
//  parsed value. Native JSON.parse gives no way to recover this, and
//  it's what makes it possible to turn a lint issue's object path
//  (e.g. "screens[1].layout.children[3].type", built by walking the
//  already-parsed object) back into a real position in the raw text --
//  instantly, client-side, matching the exact [Line X, Column Y] format
//  Meta's own validator returns from a real server round trip.
function parseJsonWithPositions(text) {
  let i = 0;
  const len = text.length;
  let line = 1, col = 1;

  function advance(n = 1) {
    for (let k = 0; k < n; k++) {
      if (i < len && text[i] === '\n') { line++; col = 1; } else { col++; }
      i++;
    }
  }
  function skipWs() {
    while (i < len && /\s/.test(text[i])) advance();
  }
  function pos() { return { line, column: col }; }

  function parseValue() {
    skipWs();
    const start = pos();
    const ch = text[i];
    let value, positions = null;
    if (ch === '{') {
      const r = parseObject(); value = r.value; positions = r.positions;
    } else if (ch === '[') {
      const r = parseArray(); value = r.value; positions = r.positions;
    } else if (ch === '"') {
      value = parseString();
    } else if (text.startsWith('true', i)) {
      value = true; advance(4);
    } else if (text.startsWith('false', i)) {
      value = false; advance(5);
    } else if (text.startsWith('null', i)) {
      value = null; advance(4);
    } else {
      const m = /^-?\d+(\.\d+)?([eE][+-]?\d+)?/.exec(text.slice(i));
      if (!m) throw new Error(`Unexpected token at line ${line}, column ${col}`);
      value = Number(m[0]);
      advance(m[0].length);
    }
    return { value, start, positions };
  }

  function parseString() {
    advance(); // opening quote
    let s = '';
    while (i < len && text[i] !== '"') {
      if (text[i] === '\\') {
        const next = text[i + 1];
        if (next === 'u') {
          s += String.fromCharCode(parseInt(text.slice(i + 2, i + 6), 16));
          advance(6);
        } else {
          s += ({ n: '\n', t: '\t', r: '\r', '"': '"', '\\': '\\', '/': '/', b: '\b', f: '\f' })[next] ?? next;
          advance(2);
        }
      } else {
        s += text[i];
        advance();
      }
    }
    advance(); // closing quote
    return s;
  }

  function parseObject() {
    const positions = {};
    const value = {};
    advance(); // {
    skipWs();
    if (text[i] === '}') { advance(); return { value, positions }; }
    while (i < len) {
      skipWs();
      const keyStart = pos();
      const key = parseString();
      skipWs();
      advance(); // :
      const { value: v, start: valueStart, positions: childPositions } = parseValue();
      value[key] = v;
      positions[key] = { keyStart, valueStart, children: childPositions };
      skipWs();
      if (text[i] === ',') { advance(); continue; }
      break;
    }
    skipWs();
    advance(); // }
    return { value, positions };
  }

  function parseArray() {
    const positions = [];
    const value = [];
    advance(); // [
    skipWs();
    if (text[i] === ']') { advance(); return { value, positions }; }
    while (i < len) {
      const { value: v, start: valueStart, positions: childPositions } = parseValue();
      value.push(v);
      positions.push({ valueStart, children: childPositions });
      skipWs();
      if (text[i] === ',') { advance(); skipWs(); continue; }
      break;
    }
    skipWs();
    advance(); // ]
    return { value, positions };
  }

  return parseValue(); // { value, start, positions }
}

// Walks a position tree using the same "screens[1].layout.children[3].type"
// style path lint issues already carry, returning {line, column} for that
// exact key (or its value, if preferValue is set — see below). Returns
// null if the path can't be resolved (e.g. the JSON was edited between
// parses) -- callers should treat that as "no location available"
// rather than fail.
//
// preferValue matters for exact parity with Meta's own column numbers:
// Meta points at the KEY's position for "property not allowed" and
// "invalid value for 'type'" (the problem IS that key/field), but at
// the VALUE's position for "expected type X but found Y" (the problem
// is specifically what's on the right of the colon).
function resolvePathPosition(rootPositions, path, preferValue = false) {
  if (!rootPositions || !path) return null;
  const tokens = path.match(/[^.[\]]+|\[\d+\]/g) || [];
  let node = { children: rootPositions };
  for (const tok of tokens) {
    if (!node || !node.children) return null;
    if (tok.startsWith('[')) {
      const idx = parseInt(tok.slice(1, -1), 10);
      node = node.children[idx];
    } else {
      node = node.children[tok];
    }
  }
  if (!node) return null;
  return preferValue
    ? (node.valueStart ?? node.keyStart ?? null)
    : (node.keyStart ?? node.valueStart ?? null);
}

// Field names WhatsApp's own data_exchange protocol already uses at the
// top level of what a screen submits (action, screen, flow_token, version)
// or reserves in the payload (data). A form field named exactly one of
// these gets silently merged into/overwritten by the protocol's own field
// of the same name when the screen submits — the field never reaches your
// endpoint with the value the user actually entered.
const RESERVED_FIELD_NAMES = ['action', 'screen', 'data', 'flow_token', 'version'];

// Component types whose text content can contain ${data.xxx} placeholders
// and are subject to Meta's known limitation: a single string with more
// than one placeholder often only resolves the first one, leaving the
// rest as literal "${data.xxx}" text in what the user sees.
const TEXT_BEARING_TYPES = new Set([
  'textbody', 'text-body', 'text', 'textheading', 'text-heading',
  'textsubheading', 'text-subheading', 'richtext', 'rich-text',
]);
const PLACEHOLDER_RE = /\$\{data\.[^}]+\}/g;

// Meta can report has_key: true (or a key_fingerprint) while
// business_public_key_signature_status says the key on file is broken --
// e.g. 'MISMATCH' means Meta has *something* registered, but it doesn't
// verify, so flows relying on it will fail. Treat those statuses as NOT
// done so the Set Up / Replace Key option stays visible instead of being
// hidden just because Meta has *a* key, working or not.
const UNHEALTHY_KEY_STATUSES = ['MISMATCH', 'mismatch'];
function isEncryptionKeyHealthy(data) {
  if (!data) return false;
  // local_key_valid is only present once the backend has been updated to
  // include it (see FlowController::get_encryption_status). Treat it as
  // healthy-by-default (undefined -> true) so this doesn't regress older
  // API responses -- but once present, a false here means our own stored
  // private key doesn't even parse, regardless of what Meta reports.
  if (data.local_key_valid === false) return false;
  if (UNHEALTHY_KEY_STATUSES.includes(data.key_status)) return false;
  return !!data.has_key ||
    ['ACTIVE', 'active', 'ENABLED', 'enabled'].includes(data.key_status) ||
    !!data.key_fingerprint;
}

/**
 * Lightweight, local schema lint for a WhatsApp Flow JSON definition.
 * This is NOT a substitute for Meta's server-side validation, but catches
 * common structural mistakes instantly without a round trip.
 * Returns an array of { message, path } objects (empty if no issues found).
 */
function lintFlowJson(parsed) {
  const issues = [];
  if (!parsed || typeof parsed !== 'object') return issues;

  if (!parsed.version) {
    issues.push({ message: 'Missing required top-level "version" field', path: 'version' });
  }

  const screens = Array.isArray(parsed.screens) ? parsed.screens : null;
  if (!screens) {
    issues.push({ message: 'Missing or invalid top-level "screens" array', path: 'screens' });
    return issues;
  }
  if (screens.length === 0) {
    issues.push({ message: '"screens" array is empty — a flow needs at least one screen', path: 'screens' });
  }

  const screenIds = new Set();
  screens.forEach((screen, sIdx) => {
    const path = `screens[${sIdx}]`;
    if (!screen.id) {
      issues.push({ message: 'Screen is missing required "id" field', path });
    } else if (screenIds.has(screen.id)) {
      issues.push({ message: `Duplicate screen id "${screen.id}"`, path: `${path}.id` });
    } else {
      screenIds.add(screen.id);
    }
    if (!screen.title) {
      issues.push({ message: 'Screen is missing a "title" field', path: `${path}.title` });
    }
    if (!screen.layout || !Array.isArray(screen.layout.children)) {
      issues.push({ message: 'Screen is missing "layout.children" array', path: `${path}.layout` });
      return;
    }
    walkComponents(screen.layout.children, `${path}.layout.children`, issues, parsed);

    // ── Screen-level structural rules (not per-component) ──────
    // Meta enforces some rules across a whole screen's tree, not on any
    // single component in isolation -- "at most one Footer per screen"
    // is the most common one to hit by accident (e.g. adding a second
    // "Search" button as its own Footer instead of a Form action).
    // Matches Meta's own message and pointing convention exactly: it
    // flags each EXTRA Footer's own ".type", not the screen as a whole.
    const footerPaths = collectComponentPaths(screen.layout.children, `${path}.layout.children`, 'footer');
    if (footerPaths.length > 1) {
      footerPaths.slice(1).forEach(fp => {
        issues.push({
          message: `Maximum number of Footer allowed per screen is 1 but found ${footerPaths.length}.`,
          path: `${fp}.type`,
        });
      });
    }
  });

  // Cross-check terminal screen
  const terminals = screens.filter(s => s.terminal === true);
  if (screens.length > 0 && terminals.length === 0) {
    issues.push({ message: 'No screen marked as "terminal": true — the flow needs an exit screen', path: 'screens' });
  }

  return issues;
}

// Recursively finds every component of a given type (case-insensitive)
// within a subtree, returning each one's exact path -- e.g. for
// checking Meta's "at most one Footer per screen" rule, which needs to
// know about every Footer's location, not just whether one exists.
// Formats every current error (local lint + Meta's own server-validated
// ones, whichever are present) into copyable text, grouping identical
// messages together with each occurrence's path/location listed
// underneath -- the same convention Meta's own error display uses,
// useful for pasting into a bug report or support ticket.
function buildErrorsText({ lintBlocking, lintWarnings, metaErrors }) {
  const groups = new Map(); // message -> [{ path, line, column }]
  function add(message, path, line, column) {
    if (!groups.has(message)) groups.set(message, []);
    groups.get(message).push({ path, line, column });
  }

  (lintBlocking || []).forEach(e => add(e.message, e.path, e.line, e.column));
  (lintWarnings || []).forEach(e => add(`⚠ ${e.message}`, e.path, e.line, e.column));
  (metaErrors || []).forEach(e => add(
    e.error_message ?? e.message ?? 'Unknown error',
    e.path, e.line_start, e.column_start
  ));

  const lines = [];
  for (const [message, occurrences] of groups) {
    lines.push(message);
    occurrences.forEach(o => {
      const loc = o.line ? ` [Line ${o.line}, Column ${o.column ?? '?'}]` : '';
      lines.push(`* ${o.path ?? '(no path)'}${loc}`);
    });
    lines.push('');
  }
  return lines.join('\n').trim();
}

function collectComponentPaths(children, basePath, targetType) {
  let paths = [];
  (children || []).forEach((c, i) => {
    const path = `${basePath}[${i}]`;
    if ((c?.type ?? '').toLowerCase() === targetType) paths.push(path);
    if (Array.isArray(c?.children)) {
      paths = paths.concat(collectComponentPaths(c.children, `${path}.children`, targetType));
    }
  });
  return paths;
}

function walkComponents(children, basePath, issues, root) {
  children.forEach((comp, cIdx) => {
    const path = `${basePath}[${cIdx}]`;
    const type = (comp.type ?? '').toLowerCase();
    if (!comp.type) {
      issues.push({ message: 'Component is missing "type" field', path });
      return;
    }

    // ── Invalid / misspelled component type ───────────────────
    // Matches Meta's own validator's exact wording for this exact
    // mistake, computed instantly and locally instead of waiting on a
    // round trip to Meta's servers.
    if (typeof comp.type === 'string' && !VALID_COMPONENT_TYPES.has(type)) {
      const suggestion = suggestComponentType(comp.type);
      issues.push({
        message: "Invalid value found for property 'type'."
          + (suggestion ? ` Did you mean "${suggestion}"?` : ''),
        path: `${path}.type`,
      });
    }

    const rule = COMPONENT_RULES[type];
    if (rule) {
      rule.required.forEach(field => {
        if (comp[field] === undefined || comp[field] === null || comp[field] === '') {
          issues.push({ message: `"${comp.type}" component is missing required "${field}"`, path: `${path}.${field}` });
        }
      });
    }

    // ── Property not allowed on this component type ────────────
    // Only enforced for the component types in ALLOWED_PROPS (the
    // stable, well-known ones) -- see that constant's comment for why
    // input/selection components are deliberately excluded.
    const allowed = ALLOWED_PROPS[type];
    if (allowed) {
      Object.keys(comp).forEach(key => {
        if (!allowed.includes(key)) {
          issues.push({
            message: `Property '${key}' is not allowed in '${comp.type}' component.`,
            path: `${path}.${key}`,
          });
        }
      });
    }

    // ── "visible" must be a real boolean or a bare data reference ──
    // Meta only accepts ${data.x} / ${screen.data.x} here -- NOT a
    // compound expression with operators, even though something like
    // ${data.count > 0} looks like valid dynamic logic. Anything that
    // doesn't match is treated as a plain string by Meta's own
    // validator, which is what produces both of these messages (yes,
    // Meta really does return both for the same violation).
    if ('visible' in comp) {
      const v = comp.visible;
      const isRealBoolean = typeof v === 'boolean';
      const isBareDataRef = typeof v === 'string' && VISIBLE_DYNAMIC_RE.test(v);
      if (!isRealBoolean && !isBareDataRef) {
        issues.push({
          message: "Expected property 'visible' to be of type 'boolean' but found 'string'.",
          path: `${path}.visible`,
          preferValue: true,
        });
        issues.push({
          message: "Property 'visible' should be of type 'boolean' or have dynamic data format of the form ${screen.data.your_value} or ${data.your_value}.",
          path: `${path}.visible`,
          preferValue: true,
        });
      }
    }

    // ── init-value on the wrong node ──────────────────────────
    // Meta's own examples sometimes show "init-value" directly on an
    // input component (TextInput, DatePicker, etc.) — but that's not
    // where the platform actually reads it from. Initial values belong
    // in the parent Form's "init-values" object, keyed by field name:
    //   { "type": "Form", "init-values": { "your_field": "..." }, ... }
    // A stray "init-value" here is silently ignored, not an error, so
    // the field just renders empty — no signal from Meta at all.
    if (type !== 'form' && comp['init-value'] !== undefined) {
      issues.push({
        severity: 'error',
        message: `"init-value" on a ${comp.type} is ignored here — set initial values on the parent Form instead: {"init-values": {"${comp.name ?? '<name>'}": ...}}`,
        path: `${path}.init-value`,
      });
    }

    // ── Field name collides with WhatsApp's own protocol field ─
    // A form field literally named "action", "screen", "data", etc.
    // shares a name with something the data_exchange payload already
    // uses at the top level — your endpoint can end up reading the
    // protocol's value instead of what the user typed, with nothing
    // in Meta's response to explain why the field is "wrong".
    if (typeof comp.name === 'string' && RESERVED_FIELD_NAMES.includes(comp.name)) {
      issues.push({
        severity: 'error',
        message: `Field name "${comp.name}" collides with a WhatsApp protocol field of the same name — rename it (e.g. "${comp.name}_value") to avoid it being overwritten on submit`,
        path: `${path}.name`,
      });
    }

    // ── Multiple ${data.xxx} placeholders in one text field ────
    // Known Meta limitation: a single string with 2+ placeholders often
    // only resolves the first one, leaving the rest as literal
    // "${data.xxx}" text on the user's screen — not a JSON error, so it
    // only shows up as garbled text after publishing and testing live.
    if (TEXT_BEARING_TYPES.has(type) && typeof comp.text === 'string') {
      const matches = comp.text.match(PLACEHOLDER_RE);
      if (matches && matches.length > 1) {
        issues.push({
          severity: 'warning',
          message: `This text has ${matches.length} "\${data.xxx}" placeholders in one string — Meta often only resolves the first one. Safer to split into separate ${comp.type} components, one placeholder each.`,
          path: `${path}.text`,
        });
      }
    }

    // Recurse into nested children (e.g. Form wraps inputs)
    if (Array.isArray(comp.children)) {
      walkComponents(comp.children, `${path}.children`, issues, root);
    }
  });
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.draft;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700,
      padding: '3px 10px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
      {status}
    </span>
  );
}

// ── Confirm Modal ─────────────────────────────────────────────
/**
 * Reusable confirm dialog — replaces all window.confirm() calls.
 * Props:
 *   title       – bold heading
 *   message     – body text
 *   detail      – optional monospace detail box (e.g. flow name)
 *   confirmLabel – button label (default "Confirm")
 *   variant     – 'danger' | 'warning' | 'info'  (default 'danger')
 *   onConfirm   – called when user clicks the confirm button
 *   onClose     – called on cancel / backdrop click
 */
function ConfirmModal({ title, message, detail, confirmLabel = 'Confirm', variant = 'danger', onConfirm, onClose }) {
  const ICONS = { danger: '🗑️', warning: '⚠️', info: 'ℹ️' };
  return (
    <div className="confirm-backdrop" onClick={onClose}>
      <div className={`confirm-dialog ${variant}`} onClick={e => e.stopPropagation()}>
        <div className="confirm-header">
          <div className={`confirm-icon-wrap ${variant}`}>{ICONS[variant]}</div>
          <div className="confirm-header-text">
            <div className="confirm-title">{title}</div>
            {message && <div className="confirm-message">{message}</div>}
          </div>
        </div>
        {detail && (
          <div className="confirm-body">
            <div className="confirm-detail">{detail}</div>
          </div>
        )}
        <div className="confirm-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { onConfirm(); onClose(); }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create Flow Modal ─────────────────────────────────────────
function CreateFlowModal({ bId, hasWaba, isRental, defaultEndpoint, onClose, onCreated }) {
  const [form, setForm] = useState({ fName: '', fCategory: 'OTHER', fEndpointUri: isRental ? (defaultEndpoint || '') : '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit() {
    if (!form.fName.trim()) return toast.error('Flow name is required');
    if (!hasWaba) return toast.error('Set your WABA ID in Bot Settings first');
    setBusy(true);
    try {
      const data = await flowsApi(bId, { action: 'create', ...form });
      if (data.success) { toast.success('Flow created on Meta!'); onCreated(); onClose(); }
      else toast.error(data.message ?? 'Failed to create flow');
    } catch { toast.error('Network error'); }
    setBusy(false);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Create WhatsApp Flow</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {!hasWaba && (
            <div className="flow-warn-banner" style={{ marginBottom: 16 }}>
              ⚠️ No WABA ID set. Go to <strong>Overview → Edit Bot Settings</strong> to add it first.
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Flow Name *</label>
            <input className="form-input" value={form.fName} placeholder="e.g. Customer Registration"
              onChange={e => set('fName', e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Category</label>
            <select className="form-input" value={form.fCategory} onChange={e => set('fCategory', e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">
              Data Endpoint URI
              <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6, color: 'var(--text-muted)' }}>(optional)</span>
            </label>
            <input className="form-input" value={form.fEndpointUri}
              placeholder="https://yourserver.com/flow-endpoint"
              onChange={e => set('fEndpointUri', e.target.value)} />
            {isRental ? (
              <div className="form-hint">
                Pre-filled with this platform's built-in endpoint, so your flow's health check works without you
                needing your own server. You can replace it with your own if you have one.
              </div>
            ) : (
              <div className="form-hint">Required only for flows with dynamic data. Leave blank for static flows.</div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !hasWaba}>
            {busy && <span className="spinner" />}{busy ? 'Creating…' : 'Create Flow'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Upload Public Key Modal ───────────────────────────────────
function UploadKeyModal({ bId, onClose, onUploaded }) {
  const [pubKey, setPubKey] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!pubKey.trim()) return toast.error('Paste your public key first');
    setBusy(true);
    try {
      const data = await flowsApi(bId, { action: 'upload_public_key', public_key: pubKey.trim() });
      if (data.success) {
        toast.success('Public key uploaded to Meta!');
        onUploaded();
        onClose();
      } else {
        toast.error(data.message ?? 'Upload failed');
      }
    } catch { toast.error('Network error'); }
    setBusy(false);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Upload Public Key to Meta</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="flow-warn-banner" style={{ marginBottom: 16, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}>
            ℹ️ <strong>How to generate your key pair:</strong><br />
            Run this command on your server to generate an RSA 2048-bit key pair:<br />
            <code style={{ display: 'block', marginTop: 8, padding: '6px 10px', background: '#e0e7ff', borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}>
              openssl genrsa -out private.pem 2048<br />
              openssl rsa -in private.pem -pubout -out public.pem
            </code>
            Paste the contents of <strong>public.pem</strong> below. Store <strong>private.pem</strong> securely on your server — your endpoint will use it to decrypt flow payloads.
          </div>
          <div className="form-group">
            <label className="form-label">RSA 2048-bit Public Key (PEM format) *</label>
            <textarea
              className="form-input flow-key-textarea"
              value={pubKey}
              onChange={e => setPubKey(e.target.value)}
              placeholder="-----BEGIN PUBLIC KEY-----&#10;MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKC...&#10;-----END PUBLIC KEY-----"
              rows={8}
              style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !pubKey.trim()}>
            {busy && <span className="spinner" />}{busy ? 'Uploading…' : 'Upload Public Key'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Generate Key Pair Modal ──────────────────────────────────
// Two steps: confirm (extra warning if a key already exists), then a
// one-time reveal of the private key with copy/download — we never show
// it again after this, since the server only ever returns it once.
function GenerateKeyModal({ bId, hasExistingKey, onClose, onGenerated }) {
  const [step, setStep] = useState(hasExistingKey ? 'confirm-replace' : 'confirm');
  const [busy, setBusy] = useState(false);
  const [wantReveal, setWantReveal] = useState(false); // opt-in: also show the private key for self-hosting
  const [publicKey, setPublicKey] = useState('');
  const [privateKey, setPrivateKey] = useState(''); // only ever populated if wantReveal was checked
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  async function generate(force = false) {
    setBusy(true);
    try {
      const data = await flowsApi(bId, { action: 'generate_key_pair', force, reveal: wantReveal });
      if (data.success) {
        setPublicKey(data.public_key || '');
        setPrivateKey(data.private_key || '');
        setStep(data.private_key ? 'reveal' : 'done');
        onGenerated();
      } else {
        toast.error(data.message ?? 'Could not generate key pair');
      }
    } catch { toast.error('Network error'); }
    setBusy(false);
  }

  function downloadPem(content, filename) {
    const blob = new Blob([content], { type: 'application/x-pem-file' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const revealPending = step === 'reveal' && !savedConfirmed;

  return (
    <div className="modal-backdrop" onClick={revealPending ? undefined : onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            {step === 'reveal' ? 'Your Key Pair Is Ready' : step === 'done' ? 'Key Pair Ready' : 'Generate Key Pair'}
          </div>
          {!revealPending && <button className="modal-close" onClick={onClose}>✕</button>}
        </div>

        <div className="modal-body">
          {(step === 'confirm' || step === 'confirm-replace') && (
            <>
              {step === 'confirm' && (
                <div className="flow-warn-banner" style={{ marginBottom: 16, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}>
                  ℹ️ We'll generate a real RSA 2048-bit key pair for you — no terminal or openssl commands needed. By
                  default it's fully automatic: the private key is saved securely on this server and the public key
                  is uploaded to Meta for you — nothing further to do.
                </div>
              )}

              {step === 'confirm-replace' && (
                <div className="flow-warn-banner" style={{ marginBottom: 16, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>
                  ⚠️ <strong>This bot already has a key pair.</strong> Generating a new one replaces it — until the new
                  public key is uploaded and confirmed by Meta (which happens automatically here), Flow encryption for
                  this bot could briefly stop working. Only do this if your current key is lost, compromised, or
                  you're intentionally rotating it.
                </div>
              )}

              {step === 'confirm' && (
                <p style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: 16 }}>
                  Your bot has no key pair yet, so this is safe to run — it won't affect anything that's currently working.
                </p>
              )}

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-mid)', cursor: 'pointer', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
                <input type="checkbox" checked={wantReveal} onChange={e => setWantReveal(e.target.checked)} style={{ marginTop: 3 }} />
                <span>
                  <strong>I want to run my own server for this instead of this platform's built-in one.</strong>{' '}
                  Show me the private key once, right after generating, so I can copy it into my own Custom Endpoint.
                </span>
              </label>
            </>
          )}

          {step === 'reveal' && (
            <>
              <div className="flow-warn-banner" style={{ marginBottom: 16, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}>
                ✅ Both keys are ready. The public key has already been sent to Meta for you — nothing more to do
                there. You get both keys below since you asked to self-host — some people copy their private key
                straight into their endpoint's own files so their own code can decrypt Flow data directly.
              </div>

              <div className="form-group">
                <label className="form-label">Public Key (PEM format) — already uploaded to Meta</label>
                <textarea
                  readOnly
                  className="form-input flow-key-textarea"
                  value={publicKey}
                  rows={5}
                  style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
                  onClick={e => e.target.select()}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" className="btn btn-outline" style={{ fontSize: 12 }}
                    onClick={() => { navigator.clipboard?.writeText(publicKey); toast.success('Public key copied'); }}>
                    📋 Copy Public Key
                  </button>
                  <button type="button" className="btn btn-outline" style={{ fontSize: 12 }}
                    onClick={() => downloadPem(publicKey, 'public.pem')}>
                    ⬇ Download public.pem
                  </button>
                </div>
              </div>

              <div className="flow-warn-banner" style={{ marginTop: 16, marginBottom: 16, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>
                🔒 <strong>Save the private key now — we can't show it to you again after you close this.</strong> This
                is the one that decrypts real customer data. Keep it somewhere safe (a password manager, or securely
                on your own server if that's where you're using it) — never somewhere public or shared.
              </div>
              <div className="form-group">
                <label className="form-label">Private Key (PEM format) — for your own endpoint</label>
                <textarea
                  readOnly
                  className="form-input flow-key-textarea"
                  value={privateKey}
                  rows={8}
                  style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
                  onClick={e => e.target.select()}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button type="button" className="btn btn-outline" style={{ fontSize: 12 }}
                  onClick={() => { navigator.clipboard?.writeText(privateKey); toast.success('Private key copied'); }}>
                  📋 Copy Private Key
                </button>
                <button type="button" className="btn btn-outline" style={{ fontSize: 12 }}
                  onClick={() => downloadPem(privateKey, 'private.pem')}>
                  ⬇ Download private.pem
                </button>
              </div>

              <div className="flow-warn-banner" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}>
                💡 It's also already saved on this server either way — you only need your own copy if you're
                deliberately running a separate Custom Endpoint or external server.
              </div>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-mid)', cursor: 'pointer', marginTop: 12 }}>
                <input type="checkbox" checked={savedConfirmed} onChange={e => setSavedConfirmed(e.target.checked)} style={{ marginTop: 3 }} />
                I've saved a copy of my private key somewhere safe.
              </label>
            </>
          )}

          {step === 'done' && (
            <>
              <div className="flow-warn-banner" style={{ marginBottom: 16, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}>
                ✅ <strong>Done — nothing more to do.</strong> The private key is saved securely on this server, and
                the public key has already been uploaded to Meta for you.
              </div>
              {publicKey && (
                <div className="form-group">
                  <label className="form-label">Public Key (PEM format) — already uploaded to Meta</label>
                  <textarea
                    readOnly
                    className="form-input flow-key-textarea"
                    value={publicKey}
                    rows={5}
                    style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
                    onClick={e => e.target.select()}
                  />
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    Shown just for confirmation of what got sent to Meta — the public key isn't sensitive. The
                    private key isn't shown here or anywhere else; it stays on this server.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {step === 'confirm' && (
            <>
              <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={() => generate(false)} disabled={busy}>
                {busy && <span className="spinner" />}{busy ? 'Generating…' : 'Generate Key Pair'}
              </button>
            </>
          )}
          {step === 'confirm-replace' && (
            <>
              <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn btn-danger" onClick={() => generate(true)} disabled={busy}>
                {busy && <span className="spinner" />}{busy ? 'Generating…' : 'Replace Existing Key'}
              </button>
            </>
          )}
          {step === 'reveal' && (
            <button className="btn btn-primary" onClick={onClose} disabled={!savedConfirmed}>
              Done — Close
            </button>
          )}
          {step === 'done' && (
            <button className="btn btn-primary" onClick={onClose}>
              Done — Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Health Check Result Panel (mirrors Meta's UI exactly) ─────
// Shows: Actual result | Expected result | Resolution tabs
function HealthCheckResultPanel({ result, onRetry, checking }) {
  const [resultTab, setResultTab] = useState('actual'); // actual|expected|resolution

  if (!result) return null;

  const passed = result.passed;
  const actual = result.actual_result;
  const expected = result.expected_result;
  const resolution = result.resolution;

  return (
    <div className="hc-result-wrap">
      {/* Header row — mirrors Meta's "Endpoint is not active / Health check passed" banner */}
      <div className={`hc-status-banner ${passed ? 'hc-passed' : 'hc-failed'}`}>
        <div>
          <div className="hc-status-title">
            {passed ? 'Endpoint is active' : 'Endpoint is not active'}
          </div>
          <div className="hc-status-sub">
            {passed
              ? 'Health check passed — Meta can reach your endpoint'
              : 'Failed to receive expected HTTP response'}
          </div>
        </div>
        <button
          className={`btn ${passed ? 'btn-success' : 'btn-outline'}`}
          onClick={onRetry}
          disabled={checking}
          style={{ flexShrink: 0 }}>
          {checking && <span className="spinner spinner-dark" />}
          {passed ? '✓ Health check passed' : (checking ? 'Checking…' : 'Run Health check')}
        </button>
      </div>

      {/* Tabs: Actual result | Expected result | Resolution */}
      {!passed && (
        <div className="hc-tabs-wrap">
          <div className="hc-tabs">
            <button className={`hc-tab ${resultTab === 'actual' ? 'active' : ''}`}    onClick={() => setResultTab('actual')}>Actual result</button>
            <button className={`hc-tab ${resultTab === 'expected' ? 'active' : ''}`}  onClick={() => setResultTab('expected')}>Expected result</button>
            <button className={`hc-tab ${resultTab === 'resolution' ? 'active' : ''}`}onClick={() => setResultTab('resolution')}>Resolution</button>
          </div>

          <div className="hc-tab-body">
            {resultTab === 'actual' && (
              <div>
                <div className="hc-error-pill">
                  <span className="hc-block-icon">⊘</span>
                  Failed to receive expected HTTP response
                </div>
                {actual && (
                  <div className="hc-code-block">
                    {actual.status_code != null && (
                      <div>Status code: <span className="hc-status-code">{actual.status_code}</span></div>
                    )}
                    {actual.body && (
                      <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#64748b', fontSize: 12 }}>
                        Body: {actual.body}
                      </div>
                    )}
                    {!actual.status_code && !actual.body && actual.error_msg && (
                      <div style={{ color: '#64748b', fontSize: 12 }}>{actual.error_msg}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {resultTab === 'expected' && (
              <div>
                <div className="hc-code-block">
                  <div>Status code: <span className="hc-status-code-green">{expected.status_code}</span></div>
                  <div style={{ marginTop: 6, color: '#64748b', fontSize: 12 }}>
                    Body: <span style={{ fontFamily: 'monospace' }}>{expected.body}</span>
                  </div>
                </div>
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  Your endpoint must respond to Meta's encrypted ping with the above JSON.
                  Decrypt the incoming payload with your RSA private key, then return:
                  <code style={{ display: 'block', marginTop: 8, padding: '8px 12px', background: '#f8fafc', borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}>
                    {`{"data":{"status":"active","version":"v3.0"}}`}
                  </code>
                </div>
              </div>
            )}

            {resultTab === 'resolution' && (
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {resolution}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Flow Editor (full-screen overlay) ────────────────────────
function FlowEditor({ flow, bId, bot, isRental, defaultEndpoint, onClose, onSaved }) {
  const [name,           setName]          = useState(flow.fName        ?? '');
  const [category,       setCategory]      = useState(flow.fCategory    ?? 'OTHER');
  const [endpoint,       setEndpoint]      = useState(flow.fEndpointUri || (isRental ? (defaultEndpoint || '') : ''));
  const [jsonDef,        setJsonDef]       = useState(flow.fJsonDef     ?? '');
  const [preview,        setPreview]       = useState(null);
  const [previewErr,     setPreviewErr]    = useState('');
  const [saving,         setSaving]        = useState(false);
  const [publishing,     setPublishing]    = useState(false);

  // Health check state
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthResult,   setHealthResult]   = useState(null); // null = not run yet

  const [showLogs,       setShowLogs]       = useState(false);

  // Encryption / public key state
  const [encStatus,      setEncStatus]     = useState(null);  // null=not loaded, object=loaded
  const [encLoading,     setEncLoading]    = useState(false);
  const [showKeyModal,   setShowKeyModal]  = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [keyMarkedDone,  setKeyMarkedDone] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false); // manual override if API can't confirm

  const [validating,     setValidating]    = useState(false);
  const [metaErrors,     setMetaErrors]    = useState(null);
  const [lintErrors,     setLintErrors]    = useState([]);
  const [healthStatus,   setHealthStatus]  = useState(null);
  const [activeStep,     setActiveStep]    = useState(null);
  const [tab,            setTab]           = useState('editor');
  const textRef       = useRef(null);
  const gutterRef     = useRef(null);
  const validateTimer = useRef(null);

  // Parse JSON for live preview + run local schema lint
  useEffect(() => {
    if (!jsonDef.trim()) { setPreview(null); setPreviewErr(''); setLintErrors([]); return; }
    try {
      const parsed = JSON.parse(jsonDef);
      setPreview(parsed);
      setPreviewErr('');
      const issues = lintFlowJson(parsed);

      // Attach real [Line, Column] to every issue, computed instantly
      // and locally -- matching Meta's own validator format, without
      // waiting on a round trip to their servers. Best-effort: if the
      // position parser hits something it doesn't handle, issues just
      // render without a location rather than losing the lint pass.
      try {
        const { positions } = parseJsonWithPositions(jsonDef);
        issues.forEach(issue => {
          const p = resolvePathPosition(positions, issue.path, issue.preferValue);
          if (p) { issue.line = p.line; issue.column = p.column; }
        });
      } catch { /* position parse failed -- issues still show without a location */ }

      setLintErrors(issues);
    } catch (e) {
      setPreview(null);
      setPreviewErr(e.message);
      setLintErrors([]);
    }
  }, [jsonDef]);

  // Auto-revalidate 3s after user stops typing
  useEffect(() => {
    if (!flow.fMetaFlowId || previewErr || !jsonDef.trim()) return;
    clearTimeout(validateTimer.current);
    validateTimer.current = setTimeout(() => {
      // Previously only re-checked if errors already existed -- meaning
      // a flow with zero errors that got edited into a broken state
      // never triggered a fresh check until Save/Publish was clicked.
      // Always revalidate now; local lint (instant, no round trip) has
      // already ruled out the easy mistakes, so this Meta call is only
      // spent when the JSON is at least locally plausible.
      fetchValidation();
    }, 3000);
    return () => clearTimeout(validateTimer.current);
  }, [jsonDef]); // eslint-disable-line

  // On open: fetch validation + encryption status
  useEffect(() => {
    if (flow.fMetaFlowId) fetchValidation();
    fetchEncryptionStatus();
  }, []); // eslint-disable-line

  async function fetchValidation() {
    setValidating(true);
    try {
      const data = await flowsApi(bId, { action: 'validate', fId: flow.fId });
      if (data.success) {
        setMetaErrors(data.validation_errors ?? []);
        setHealthStatus(data.health_status ?? null);
      }
    } catch {}
    setValidating(false);
  }

  async function fetchEncryptionStatus() {
    setEncLoading(true);
    try {
      const data = await flowsApi(bId, { action: 'get_encryption_status' });
      if (data.success) {
        // Deliberately NOT overwriting has_key here (a previous version
        // of this code did, and it caused a real bug: existence and
        // health got collapsed into one flag, so a corrupted-but-present
        // key read as "no key exists" and skipped the force-replace
        // confirmation in GenerateKeyModal). Keep has_key/local_key_exists
        // as raw existence signals, and carry the healthy verdict
        // separately as is_healthy.
        const normalised = {
          ...data,
          is_healthy: isEncryptionKeyHealthy(data),
        };
        setEncStatus(normalised);
        if (data.key_marked_done) setKeyMarkedDone(true);
      } else {
        setEncStatus({ has_key: false, key_status: 'unknown' });
      }
    } catch {
      setEncStatus({ has_key: false, key_status: 'unknown' });
    }
    setEncLoading(false);
  }

  function onKeyDown(e) {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = textRef.current;
      const s = ta.selectionStart, en = ta.selectionEnd;
      const v = jsonDef.substring(0, s) + '  ' + jsonDef.substring(en);
      setJsonDef(v);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 2; });
    }
  }

  // Switches to the Editor tab, places the cursor at a given [line,
  // column], and scrolls it into view -- so clicking an error (local
  // lint or Meta's own) jumps straight to it, the way a real code
  // editor or Meta's own validator does, instead of leaving the user to
  // manually count lines.
  function jumpToLine(line, column) {
    if (!line) { setTab('editor'); return; }
    const lines = jsonDef.split('\n');
    let offset = 0;
    for (let i = 0; i < line - 1 && i < lines.length; i++) offset += lines[i].length + 1;
    offset += Math.max(0, (column ?? 1) - 1);

    setTab('editor');
    requestAnimationFrame(() => {
      const ta = textRef.current;
      if (!ta) return;
      ta.focus();
      ta.selectionStart = ta.selectionEnd = Math.min(offset, jsonDef.length);
      const style = window.getComputedStyle(ta);
      const lineHeight = parseFloat(style.lineHeight) || 20;
      ta.scrollTop = Math.max(0, (line - 1) * lineHeight - ta.clientHeight / 2);
      if (gutterRef.current) gutterRef.current.scrollTop = ta.scrollTop;
    });
  }

  function formatJson() {
    try   { setJsonDef(JSON.stringify(JSON.parse(jsonDef), null, 2)); toast.success('JSON formatted'); }
    catch { toast.error('Invalid JSON — fix errors first'); }
  }

  async function save() {
    if (!name.trim()) return toast.error('Flow name required');
    setSaving(true);
    try {
      const data = await flowsApi(bId, {
        action: 'update_json', fId: flow.fId,
        fName: name, fCategory: category, fEndpointUri: endpoint, fJsonDef: jsonDef,
      });
      if (data.success) {
        toast.success('Flow saved — re-validating…');
        await fetchValidation();
        const newStatus = flow.fStatus === 'published' ? 'draft' : flow.fStatus;
        onSaved({ ...flow, fName: name, fCategory: category, fEndpointUri: endpoint, fJsonDef: jsonDef, fStatus: newStatus });
      } else toast.error(data.message ?? 'Save failed');
    } catch { toast.error('Network error'); }
    setSaving(false);
  }

  async function publish() {
    if (!flow.fMetaFlowId) return toast.error('Flow not yet linked to Meta');
    if (metaErrors && metaErrors.length > 0) return toast.error('Fix all JSON errors before publishing');
    setConfirmPublish(true);
  }

  async function doPublish() {
    setPublishing(true);
    try {
      await flowsApi(bId, {
        action: 'update_json', fId: flow.fId,
        fName: name, fCategory: category, fEndpointUri: endpoint, fJsonDef: jsonDef,
      });
      const data = await flowsApi(bId, { action: 'publish', fId: flow.fId });
      if (data.success) { toast.success('Flow published! 🚀'); onClose(); }
      else toast.error(data.message ?? 'Publish failed');
    } catch { toast.error('Network error'); }
    setPublishing(false);
  }

  // ── Real health check — calls Meta API, shows actual/expected/resolution ──
  async function runHealthCheck() {
    if (!flow.fMetaFlowId) return toast.error('Flow is not linked to Meta yet');
    setHealthChecking(true);
    setHealthResult(null);
    try {
      const data = await flowsApi(bId, { action: 'health_check', fId: flow.fId, fEndpointUri: endpoint });
      setHealthResult(data);
      if (data.passed) {
        toast.success('Health check passed ✓');
      } else {
        toast.error('Health check failed — see details below');
      }
    } catch {
      toast.error('Network error during health check');
      setHealthResult({ passed: false, actual_result: { error_msg: 'Network error' }, resolution: 'Could not reach the server. Check your connection.' });
    }
    setHealthChecking(false);
  }

  const isDeprecated = flow.fStatus === 'deprecated';
  const canEdit      = !isDeprecated;

  const localJsonErr = previewErr && jsonDef.trim() ? 1 : 0;
  const metaErrCount = metaErrors ? metaErrors.length : 0;
  // Warnings (currently just the multi-placeholder heuristic) are shown but
  // don't block Publish — they flag a known Meta quirk that doesn't always
  // fire, unlike the other lint checks which are unambiguous mistakes.
  const lintBlocking = lintErrors.filter(e => e.severity !== 'warning');
  const lintWarnings = lintErrors.filter(e => e.severity === 'warning');
  const lintErrCount = lintBlocking.length;
  const totalErrors  = (metaErrors !== null ? metaErrCount : localJsonErr) + lintErrCount;
  const hasErrors    = totalErrors > 0;

  const endpointIsSet = !!endpoint.trim();
  const screenCount   = preview?.screens?.length ?? 0;
  // encStatus.is_healthy is computed by fetchEncryptionStatus (see
  // isEncryptionKeyHealthy) -- it's false when Meta flags the key on file
  // as broken (key_status: 'MISMATCH') or when our local private key
  // doesn't validate, even though encStatus.has_key (Meta's raw signal)
  // may still be true. Use is_healthy here, not has_key -- has_key only
  // means "something exists", not "it works". See hasExistingKey below
  // for why that distinction matters.
  const keyIsDone = keyMarkedDone || !!encStatus?.is_healthy;

  // ── Step definitions ──────────────────────────────────────
  // "done" is now driven by real data, not hardcoded booleans
  const STEP_HELP = [
    {
      label: 'Set endpoint URI',
      done:  endpointIsSet,
      help:  endpointIsSet
        ? 'This will identify the endpoint that the Flow will exchange data with.'
        : 'Enter the URL of your server that handles WhatsApp Flow data exchange requests. Your server must accept POST requests with an AES-encrypted body from Meta.',
      action: { fn: 'edit_endpoint' },
    },
    {
      label: 'Add phone number',
      done:  !!flow.fMetaFlowId,
      help:  flow.fMetaFlowId
        ? 'A phone number is already linked to your WhatsApp business account.'
        : 'Your WhatsApp Business phone number must be linked to this flow. This happens automatically when you create a flow through this platform using your connected WABA.',
      action: flow.fMetaFlowId
        ? { label: 'Manage phone numbers', url: 'https://business.facebook.com/wa/manage/phone-numbers/' }
        : null,
    },
    {
      label: 'Sign public key',
      done:  encLoading ? false : keyIsDone,
      loading: encLoading,
      help:  encLoading
        ? 'Checking encryption status…'
        : keyIsDone
          ? `A public key has already been signed.${encStatus?.key_fingerprint ? `\n\nFingerprint: ${encStatus.key_fingerprint}` : ''}${encStatus?.key_uploaded_at ? `\nUploaded: ${new Date(encStatus.key_uploaded_at * 1000).toLocaleDateString()}` : ''}`
          : 'You need to generate an RSA 2048-bit key pair and upload the public key to Meta. This lets Meta encrypt flow data exchange requests that only your server can decrypt.\n\nClick "Upload Public Key" below to get started.',
      action: encLoading ? null : keyIsDone
        ? { label: 'Replace key', fn: 'upload_key' }
        : { label: 'Upload Public Key', fn: 'upload_key' },
      secondaryAction: (!encLoading && !keyIsDone)
        ? { label: 'Key is already uploaded — mark as done', fn: 'mark_key_done' }
        : null,
    },
    {
      label: 'Connect Meta app',
      done:  !!flow.fMetaFlowId,
      help:  flow.fMetaFlowId
        ? `A Meta app is required to allow the Flow to communicate with your endpoint. Once submitted, it cannot be changed.`
        : 'Your Meta App must be connected and have the necessary permissions (whatsapp_business_messaging, whatsapp_business_management). This is configured in your Bot Settings → App Secret.',
      metaAppName: flow.fMetaFlowId ? (bot?.bName ?? null) : null,
      action: null,
    },
    {
      label: 'Health check',
      done:  healthResult?.passed === true,
      active: healthChecking,
      help:  healthResult?.passed
        ? 'Health check passed. Meta can reach and decrypt your endpoint correctly.'
        : endpointIsSet
          ? 'Run the health check to verify Meta can reach your endpoint. Meta will send an AES-encrypted ping — your server must decrypt it and respond with {"data":{"status":"active","version":"v3.0"}}.'
          : 'Set your endpoint URI first (step 1), then run the health check.',
      action: endpointIsSet && flow.fMetaFlowId ? { label: 'Run Health check now', fn: 'health' } : null,
    },
  ];

  return (
    <div className="flow-editor-overlay">

      {/* ── Top bar ── */}
      <div className="flow-editor-topbar">
        <div className="flow-editor-topbar-left">
          <button className="flow-editor-back" onClick={onClose}>← Back</button>
          <div className="flow-editor-flowname">
            {canEdit
              ? <input className="flow-editor-name-input" value={name}
                  onChange={e => setName(e.target.value)} placeholder="Flow name" />
              : <span style={{ fontWeight: 800, fontSize: 15 }}>{name}</span>
            }
            <StatusBadge status={flow.fStatus ?? 'draft'} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              · Updated {flow.fUpdatedAt
                ? new Date(flow.fUpdatedAt.replace(' ', 'T')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—'}
            </span>
          </div>
        </div>
        <div className="flow-editor-topbar-right">
          {canEdit && (
            <>
              <button className="btn btn-outline" onClick={save} disabled={saving}>
                {saving && <span className="spinner spinner-dark" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
              {flow.fMetaFlowId && (
                <button className="btn btn-primary" onClick={publish}
                  disabled={publishing || hasErrors}
                  title={hasErrors ? 'Fix all JSON errors before publishing' : ''}>
                  {publishing && <span className="spinner" />}
                  {publishing ? 'Publishing…' : 'Publish'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Tab strip ── */}
      <div className="flow-editor-tabs">
        <button
          className={`flow-editor-tab ${tab === 'errors' ? 'active' : ''} ${hasErrors ? 'tab-has-errors' : ''}`}
          onClick={() => setTab('errors')}>
          Flow JSON Errors
          <span className={`flow-err-count ${hasErrors ? 'has-err' : 'no-err'}`}>
            {validating ? '…' : totalErrors}
          </span>
        </button>
        <button className={`flow-editor-tab ${tab === 'editor' ? 'active' : ''}`} onClick={() => setTab('editor')}>
          Editor
        </button>
        <button className={`flow-editor-tab ${tab === 'actions' ? 'active' : ''}`} onClick={() => setTab('actions')}>
          Actions
        </button>
        <button className={`flow-editor-tab ${tab === 'endpoint' ? 'active' : ''}`} onClick={() => setTab('endpoint')}>
          Endpoint
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, paddingRight: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Category</span>
            {canEdit
              ? <select className="form-input" style={{ padding: '4px 8px', fontSize: 12, height: 30 }}
                  value={category} onChange={e => setCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
              : <span style={{ fontSize: 12, fontWeight: 700 }}>{category.replace(/_/g, ' ')}</span>
            }
          </div>
          {flow.fMetaFlowId && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
              ID: {flow.fMetaFlowId}
            </span>
          )}
        </div>
      </div>

      {/* ── Flow JSON Errors tab ── */}
      {tab === 'errors' && (
        <div className="flow-editor-panel-body">
          {!validating && totalErrors > 0 && (
            <div className="flow-errors-header">
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                {totalErrors} error{totalErrors !== 1 ? 's' : ''} found
              </span>
              <button
                className="btn btn-outline"
                style={{ fontSize: 12, padding: '5px 10px' }}
                onClick={() => {
                  const text = buildErrorsText({ lintBlocking, lintWarnings, metaErrors });
                  copyToClipboard(text);
                  toast.success('Errors copied to clipboard');
                }}
              >
                📋 Copy Errors
              </button>
            </div>
          )}

          {validating && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
              <span className="spinner spinner-dark" /> Fetching validation errors from Meta…
            </div>
          )}

          {/* Local schema lint — runs instantly, no round trip to Meta */}
          {!validating && lintBlocking.length > 0 && (
            <div className="flow-error-list" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                Local schema check ({lintBlocking.length})
              </div>
              {lintBlocking.map((err, i) => (
                <div key={`lint-${i}`} className="flow-error-item flow-error-item-clickable"
                  onClick={() => jumpToLine(err.line, err.column)} title="Click to jump to this line">
                  <div className="flow-error-message">{err.message}</div>
                  <div className="flow-error-detail-row">
                    <span className="flow-error-icon">⊗</span>
                    <span className="flow-error-path">{err.path}</span>
                    {err.line && (
                      <span className="flow-error-loc">[Line {err.line}, Column {err.column}]</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Non-blocking heuristic warnings — shown, but don't gate Publish */}
          {!validating && lintWarnings.length > 0 && (
            <div className="flow-error-list" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#9a7b00', textTransform: 'uppercase', marginBottom: 6 }}>
                ⚠ Worth checking ({lintWarnings.length})
              </div>
              {lintWarnings.map((err, i) => (
                <div key={`lint-warn-${i}`} className="flow-error-item flow-error-item-clickable" style={{ borderColor: '#fde8b8', background: '#fffbf0' }}
                  onClick={() => jumpToLine(err.line, err.column)} title="Click to jump to this line">
                  <div className="flow-error-message">{err.message}</div>
                  <div className="flow-error-detail-row">
                    <span className="flow-error-icon" style={{ color: '#9a7b00' }}>!</span>
                    <span className="flow-error-path">{err.path}</span>
                    {err.line && (
                      <span className="flow-error-loc">[Line {err.line}, Column {err.column}]</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!validating && metaErrors === null && !flow.fMetaFlowId && lintErrors.length === 0 && (
            <div className="flow-no-errors-msg">
              Flow is not linked to Meta yet — create it first to get validation.
            </div>
          )}
          {!validating && metaErrors !== null && metaErrors.length === 0 && lintBlocking.length === 0 && (
            <div className="flow-no-errors-msg">
              <span style={{ fontSize: 28 }}>✅</span>
              <div>No JSON errors detected</div>
              {flow.fMetaFlowId && (
                <button className="btn btn-outline" style={{ marginTop: 12 }} onClick={fetchValidation}>
                  Re-check
                </button>
              )}
            </div>
          )}
          {!validating && metaErrors && metaErrors.length > 0 && (
            <div className="flow-error-list">
              {metaErrors.map((err, i) => (
                <div key={i} className={`flow-error-item${err.line_start ? ' flow-error-item-clickable' : ''}`}
                  onClick={err.line_start ? () => jumpToLine(err.line_start, err.column_start) : undefined}
                  title={err.line_start ? 'Click to jump to this line' : undefined}>
                  <div className="flow-error-message">{err.error_message ?? err.message ?? 'Unknown error'}</div>
                  {err.path && (
                    <div className="flow-error-detail-row">
                      <span className="flow-error-icon">⊗</span>
                      <span className="flow-error-path">{err.path}</span>
                      {(err.line_start || err.column_start) && (
                        <span className="flow-error-loc">
                          [Line {err.line_start ?? '?'}, Column {err.column_start ?? '?'}]
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={fetchValidation} disabled={validating}>
                  ↻ Re-validate
                </button>
                <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={() => setTab('editor')}>
                  Open Editor →
                </button>
              </div>
            </div>
          )}
          {!validating && metaErrors === null && localJsonErr > 0 && (
            <div className="flow-error-list">
              <div className="flow-error-item">
                <div className="flow-error-message">JSON Parse Error</div>
                <div className="flow-error-detail-row">
                  <span className="flow-error-icon">⊗</span>
                  <span className="flow-error-path">{previewErr}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Editor tab ── */}
      {tab === 'editor' && (
        <div className="flow-editor-body">
          <div className="flow-editor-pane flow-editor-code-pane">
            <div className="flow-editor-pane-header">
              <div className="flow-editor-file-tab">
                <span className="flow-editor-file-icon">{ }</span>
                flow.json
              </div>
              <div className="flow-editor-pane-actions">
                {canEdit && (
                  <button className="flow-editor-fmt-btn" onClick={formatJson}>⌥⇧F Format</button>
                )}
              </div>
            </div>
            <div className="flow-code-editor-wrap">
              <div className="flow-code-gutter" ref={gutterRef} style={{ overflowY: 'hidden' }}>
                <div className="flow-code-line-numbers">
                  {(jsonDef || '').split('\n').map((_, i) => (
                    <span key={i}>{i + 1}</span>
                  ))}
                </div>
              </div>
              <textarea
                ref={textRef}
                className="flow-code-editor"
                value={jsonDef}
                onChange={e => setJsonDef(e.target.value)}
                onKeyDown={onKeyDown}
                onScroll={e => { if (gutterRef.current) gutterRef.current.scrollTop = e.target.scrollTop; }}
                readOnly={!canEdit}
                placeholder={`{\n  "version": "7.1",\n  "screens": [...]\n}`}
                spellCheck={false}
              />
            </div>
            <div className="flow-editor-status-bar">
              <span>{jsonDef ? jsonDef.split('\n').length : 0} lines</span>
              <span className="flow-editor-status-sep">|</span>
              <span>JSON</span>
              <span className="flow-editor-status-sep">|</span>
              {previewErr && jsonDef.trim()
                ? <span className="flow-editor-status-err">⚠ Parse Error</span>
                : <span className="flow-editor-status-ok">✓ Valid JSON</span>
              }
              {previewErr && jsonDef.trim() && (
                <span className="flow-editor-status-sep" style={{ marginLeft: 'auto', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {previewErr}
                </span>
              )}
            </div>
          </div>
          <div className="flow-editor-pane flow-editor-preview-pane">
            <div className="flow-editor-pane-header">
              <span>Preview</span>
              {screenCount > 0 && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {screenCount} screen{screenCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <ErrorBoundary key={jsonDef}>
              <FlowPreview screens={preview?.screens} />
            </ErrorBoundary>
          </div>
        </div>
      )}

      {/* ── Actions tab ── */}
      {tab === 'actions' && (
        <div className="flow-editor-panel-body">
          <div className="flow-panel-card">
            <div className="flow-panel-card-title">Validation Status</div>
            <div className="flow-panel-card-row">
              <span className={`flow-error-badge ${hasErrors ? 'has-error' : 'no-error'}`}>{totalErrors}</span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 8 }}>
                {hasErrors ? 'Fix JSON errors before publishing' : 'No errors detected — ready to publish'}
              </span>
              <button className="btn btn-outline" style={{ fontSize: 12, marginLeft: 'auto' }} onClick={fetchValidation} disabled={validating}>
                {validating && <span className="spinner spinner-dark" />} Re-validate
              </button>
            </div>
          </div>
          <div className="flow-panel-card">
            <div className="flow-panel-card-title">Actions</div>
            <div className="flow-panel-card-row" style={{ gap: 10 }}>
              {canEdit && (
                <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={save} disabled={saving}>
                  {saving && <span className="spinner spinner-dark" />} Save Draft
                </button>
              )}
              {canEdit && flow.fMetaFlowId && (
                <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={publish}
                  disabled={publishing || hasErrors}
                  title={hasErrors ? 'Fix all errors first' : ''}>
                  {publishing && <span className="spinner" />} 🚀 Publish
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Endpoint tab ── */}
      {tab === 'endpoint' && (
        <div className="flow-editor-panel-body">

          {/* Health check result panel — shows when a check has been run */}
          {(healthResult || healthChecking) && (
            <HealthCheckResultPanel
              result={healthResult}
              onRetry={runHealthCheck}
              checking={healthChecking}
            />
          )}

          {/* Default banner when no check has been run yet */}
          {!healthResult && !healthChecking && (
            <div className={`flow-endpoint-banner ${endpointIsSet ? 'set' : 'unset'}`}>
              <div>
                <div className="flow-endpoint-banner-title">
                  {endpointIsSet ? 'Endpoint is set up' : 'No endpoint configured'}
                </div>
                <div className="flow-endpoint-banner-sub">
                  {endpointIsSet
                    ? 'Run health check to verify endpoint is active'
                    : 'Add your endpoint URL below, or leave blank for a static flow'}
                </div>
              </div>
              {endpointIsSet && flow.fMetaFlowId && (
                <button className="btn btn-outline" onClick={runHealthCheck} disabled={healthChecking}>
                  {healthChecking && <span className="spinner spinner-dark" />}
                  Run Health check
                </button>
              )}
            </div>
          )}

          <div className="flow-endpoint-banner set" style={{ marginTop: 12 }}>
            <div>
              <div className="flow-endpoint-banner-title">Traffic Logs</div>
              <div className="flow-endpoint-banner-sub">
                Every data_exchange call Meta sends for this Flow — decrypted request/response, which endpoint it was routed to, and any error.
              </div>
            </div>
            <button className="btn btn-outline" onClick={() => setShowLogs(true)}>View Logs</button>
          </div>

          <div className="flow-endpoint-layout">
            <div className="flow-endpoint-steps">
              {STEP_HELP.map((step, i) => (
                <div
                  key={i}
                  className={`flow-endpoint-step ${step.done ? 'done' : step.active ? 'active' : ''} ${activeStep === i ? 'selected' : ''}`}
                  onClick={() => setActiveStep(activeStep === i ? null : i)}>
                  <div className="flow-step-icon">
                    {step.loading ? '…' : step.done ? '✓' : step.active ? '…' : (i + 1)}
                  </div>
                  <span>{step.label}</span>
                </div>
              ))}
            </div>

            <div className="flow-endpoint-form">
              {activeStep !== null ? (
                /* ── Step help panel ── */
                <div className="flow-step-help-panel">
                  <div className="flow-step-help-title">{STEP_HELP[activeStep].label}</div>
                  <div className="flow-step-help-body" style={{ whiteSpace: 'pre-wrap' }}>
                    {STEP_HELP[activeStep].help}
                  </div>
                  {/* Show bot/app name selector like Meta UI does for "Connect Meta app" */}
                  {STEP_HELP[activeStep].metaAppName && (
                    <div style={{
                      marginTop: 14,
                      padding: '10px 14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      background: '#f8fafc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: 13,
                      color: 'var(--text-dark)',
                    }}>
                      <span>{STEP_HELP[activeStep].metaAppName}</span>
                      <span style={{ color: '#94a3b8', fontSize: 16 }}>▾</span>
                    </div>
                  )}
                  {STEP_HELP[activeStep].action && (
                    STEP_HELP[activeStep].action.fn === 'edit_endpoint'
                      ? (
                        <div style={{ marginTop: 14 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input
                              className="form-input"
                              style={{ flex: 1 }}
                              value={endpoint}
                              onChange={e => setEndpoint(e.target.value)}
                              placeholder="https://yourserver.com/api/flow-data"
                            />
                            <button
                              className="btn btn-outline"
                              onClick={() => { save(); setActiveStep(null); }}
                              disabled={saving}>
                              {saving && <span className="spinner spinner-dark" />}
                              Submit
                            </button>
                          </div>
                        </div>
                      )
                      : STEP_HELP[activeStep].action.fn === 'health'
                        ? <button className="btn btn-outline" style={{ marginTop: 14 }}
                            onClick={() => { setActiveStep(null); runHealthCheck(); }}
                            disabled={healthChecking}>
                            {healthChecking && <span className="spinner spinner-dark" />}
                            Run Health check
                          </button>
                        : STEP_HELP[activeStep].action.fn === 'upload_key'
                          ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14, alignItems: 'flex-start' }}>
                              <button className="btn btn-primary"
                                onClick={() => { setActiveStep(null); setShowGenerateModal(true); }}>
                                ✨ Generate Key Pair for Me (recommended)
                              </button>
                              <button className="btn btn-outline"
                                onClick={() => { setActiveStep(null); setShowKeyModal(true); }}>
                                {STEP_HELP[activeStep].action.label}
                              </button>
                              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                Not sure which to pick? Generate is easier — no terminal or openssl needed.
                              </span>
                            </div>
                          : <a className="btn btn-outline" style={{ marginTop: 14, display: 'inline-flex' }}
                              href={STEP_HELP[activeStep].action.url} target="_blank" rel="noopener noreferrer">
                              {STEP_HELP[activeStep].action.label} ↗
                            </a>
                  )}
                  {/* Secondary action — e.g. manual key confirmation */}
                  {STEP_HELP[activeStep].secondaryAction && (
                    STEP_HELP[activeStep].secondaryAction.fn === 'mark_key_done'
                      ? <button
                          className="btn btn-outline"
                          style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', borderColor: '#e2e8f0' }}
                          onClick={async () => {
                            setKeyMarkedDone(true);
                            setActiveStep(null);
                            toast.success('Public key marked as done');
                            try {
                              const res = await flowsApi(bId, { action: 'mark_key_done', done: true });
                              if (!res.success) toast.error('Could not save — will reset on reload');
                            } catch { toast.error('Network error — will reset on reload'); }
                          }}>
                          ✓ {STEP_HELP[activeStep].secondaryAction.label}
                        </button>
                      : null
                  )}
                  <button className="flow-step-help-close" onClick={() => setActiveStep(null)}>✕ Close</button>
                </div>
              ) : (
                /* ── Default: endpoint input ── */
                <>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
                    Click any step on the left to see what it means and what to do.
                    Set your endpoint URL below and click Save, then run the health check.
                  </div>
                  <div className="form-group">
                    <label className="form-label">Endpoint URI</label>
                    {canEdit
                      ? <input className="form-input" value={endpoint}
                          onChange={e => setEndpoint(e.target.value)}
                          placeholder="https://yourserver.com/api/flow-data" />
                      : <div className="flow-endpoint-readonly">{endpoint || '(none)'}</div>
                    }
                    {isRental && canEdit && (
                      <div className="form-hint">
                        Pre-filled with this platform's built-in endpoint for rented numbers — you can replace it
                        with your own server if you have one.
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                      <button className="btn btn-outline" onClick={save} disabled={saving}>
                        {saving && <span className="spinner spinner-dark" />} Save
                      </button>
                      {endpointIsSet && flow.fMetaFlowId && (
                        <button className="btn btn-outline" onClick={runHealthCheck} disabled={healthChecking}>
                          {healthChecking && <span className="spinner spinner-dark" />}
                          Run Health check
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Key Modal */}
      {showKeyModal && (
        <UploadKeyModal
          bId={bId}
          onClose={() => setShowKeyModal(false)}
          onUploaded={() => { fetchEncryptionStatus(); }}
        />
      )}

      {/* Generate Key Pair Modal */}
      {showGenerateModal && (
        <GenerateKeyModal
          bId={bId}
          hasExistingKey={!!encStatus?.has_key || !!encStatus?.local_key_exists}
          onClose={() => setShowGenerateModal(false)}
          onGenerated={() => { fetchEncryptionStatus(); }}
        />
      )}

      {/* Publish Confirm Modal */}
      {confirmPublish && (
        <ConfirmModal
          title="Publish this flow?"
          message="Once published, this flow cannot be edited. All changes will go live immediately."
          detail={name}
          confirmLabel="🚀 Publish"
          variant="warning"
          onConfirm={doPublish}
          onClose={() => setConfirmPublish(false)}
        />
      )}
      {/* Traffic Logs Modal */}
      {showLogs && (
        <FlowLogsModal
          bId={bId}
          fId={flow.fId}
          flowName={name}
          onClose={() => setShowLogs(false)}
        />
      )}
    </div>
  );
}

// ── Flow Traffic Logs Modal ─────────────────────────────────────
function FlowLogsModal({ bId, fId, flowName, onClose }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all'); // all | error
  const [q, setQ]             = useState('');
  const [expanded, setExpanded] = useState(null);
  const [liveTail, setLiveTail] = useState(false);
  const liveTimer = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await flowsApi(bId, { action: 'logs', fId, limit: 150 });
      if (data.success) setLogs(data.logs || []);
      else if (!silent) toast.error(data.message ?? 'Could not load logs');
    } catch {
      if (!silent) toast.error('Network error loading logs');
    }
    if (!silent) setLoading(false);
  }, [bId, fId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (liveTail) {
      liveTimer.current = setInterval(() => load(true), 4000);
      return () => clearInterval(liveTimer.current);
    }
  }, [liveTail, load]);

  async function handleClear() {
    try {
      const data = await flowsApi(bId, { action: 'clear_logs', fId });
      if (data.success) { setLogs([]); toast.success('Logs cleared'); }
      else toast.error(data.message ?? 'Could not clear logs');
    } catch {
      toast.error('Network error clearing logs');
    }
  }

  const errorCount = logs.filter(l => l.lError).length;
  const filtered = logs.filter(l => {
    if (filter === 'error' && !l.lError) return false;
    if (!q.trim()) return true;
    const hay = `${l.lAction ?? ''} ${l.lScreen ?? ''} ${l.lRequestBody ?? ''} ${l.lResponseBody ?? ''} ${l.lError ?? ''}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{flowName} — Traffic Logs</div>
            {!loading && logs.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {errorCount > 0 && <span style={{ color: '#c0392b', fontWeight: 600, marginRight: 10 }}>⚠ {errorCount} error{errorCount !== 1 ? 's' : ''}</span>}
                {logs.length} total
              </div>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ overflow: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: 12, ...(filter === 'all' ? { borderColor: 'var(--primary, #2563eb)' } : {}) }}
              onClick={() => setFilter('all')}>All {logs.length}</button>
            <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: 12, ...(filter === 'error' ? { borderColor: '#c0392b', color: '#c0392b' } : {}) }}
              onClick={() => setFilter('error')}>Errors {errorCount}</button>
            <input className="form-input" style={{ flex: 1, minWidth: 180, fontSize: 12, padding: '5px 10px' }}
              placeholder="Search action, screen, request, response…" value={q} onChange={e => setQ(e.target.value)} />
            <button type="button" className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={() => setLiveTail(v => !v)} title={liveTail ? 'Auto-refreshing every 4s' : 'Auto-refresh every 4s'}>
              {liveTail ? '● Live' : 'Live tail'}
            </button>
            <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => load()}>Refresh</button>
            <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12 }} onClick={handleClear}>Clear</button>
          </div>

          {loading ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '20px 0' }}>Loading…</div>
          ) : logs.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '20px 0' }}>
              No calls logged yet — Meta hasn't hit this Flow's endpoint, or it's still using the health_check ping only.
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '20px 0' }}>No logs match this filter.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {filtered.map(log => {
                const isError = !!log.lError;
                const isOpen  = expanded === log.lId;
                return (
                  <div key={log.lId} style={{
                    border: '1.5px solid ' + (isError ? '#f3c6c2' : 'var(--border)'),
                    borderRadius: 8, background: isError ? '#fff8f7' : 'var(--bg-subtle, #fafafa)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', fontSize: 12 }}
                      onClick={() => setExpanded(isOpen ? null : log.lId)}>
                      <span style={{
                        fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: isError ? '#fdeaea' : '#e7f9f0', color: isError ? '#c0392b' : '#00875a',
                      }}>{isError ? 'ERROR' : 'OK'}</span>
                      <span style={{ fontWeight: 600 }}>{log.lAction || '(empty)'}</span>
                      {log.lScreen && <span style={{ color: 'var(--text-muted)' }}>{log.lScreen}</span>}
                      <span style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}>{formatDate(log.lCreatedAt)}</span>
                      {log.lDurationMs != null && <span style={{ color: 'var(--text-muted)' }}>{log.lDurationMs}ms</span>}
                      <span>{isOpen ? '▾' : '▸'}</span>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '0 12px 12px', fontSize: 12 }}>
                        {log.lError && (
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>Error</div>
                            <div style={{ color: '#c0392b' }}>{log.lError}</div>
                          </div>
                        )}
                        {log.lEndpointUri && (
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>Routed to</div>
                            <div style={{ wordBreak: 'break-all' }}>
                              {log.lEndpointUri} {log.lProxyStatus != null && <span style={{ color: 'var(--text-muted)' }}>(HTTP {log.lProxyStatus})</span>}
                            </div>
                          </div>
                        )}
                        {log.lRequestBody && (
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                              <div style={{ fontWeight: 600 }}>Decrypted request</div>
                              <button className="btn btn-outline" style={{ padding: '2px 8px', fontSize: 11 }}
                                onClick={() => { copyToClipboard(log.lRequestBody); toast.success('Copied'); }}>Copy</button>
                            </div>
                            <pre style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 6, padding: 8, overflow: 'auto', maxHeight: 220, margin: 0 }}>{log.lRequestBody}</pre>
                          </div>
                        )}
                        {log.lResponseBody && (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                              <div style={{ fontWeight: 600 }}>Response sent back</div>
                              <button className="btn btn-outline" style={{ padding: '2px 8px', fontSize: 11 }}
                                onClick={() => { copyToClipboard(log.lResponseBody); toast.success('Copied'); }}>Copy</button>
                            </div>
                            <pre style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 6, padding: 8, overflow: 'auto', maxHeight: 220, margin: 0 }}>{log.lResponseBody}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Simple phone-frame preview ────────────────────────────────
function FlowPreview({ screens }) {
  const [screenIdx, setScreenIdx] = useState(0);
  const [completionInfo, setCompletionInfo] = useState(null);

  // Reset if the flow's screen count changes (e.g. user is actively
  // editing) so a stale index never points past the end.
  useEffect(() => { setScreenIdx(0); setCompletionInfo(null); }, [screens?.length]);

  if (!screens || screens.length === 0) {
    return (
      <div className="flow-preview-empty">
        <div style={{ fontSize: 32, marginBottom: 8 }}>📱</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Preview will appear here as you type valid JSON</div>
      </div>
    );
  }
  const screen = screens[Math.min(screenIdx, screens.length - 1)];

  // Handles what a real button-tap would do, mirroring Meta's "Run"
  // click-through as closely as is safe to do without a real backend
  // in the loop. "navigate" is fully simulated client-side (find the
  // target screen by id, switch to it) since that's pure client-side
  // Flow structure with no side effects. "complete" and "data_exchange"
  // deliberately do NOT call your real endpoint here -- this preview can
  // be used mid-edit with fabricated data, and something like a
  // /withdraw flow's real endpoint should never fire from a preview
  // click. Instead, the resolved payload that WOULD be sent is shown,
  // so you can still confirm ${data.x} / ${form.x} placeholders resolve
  // the way you expect.
  function handleAction(action, dataSchema, answers) {
    if (!action || !action.name) return;
    const resolvedPayload = resolvePayloadObject(action.payload, dataSchema, answers);

    if (action.name === 'navigate' && action.next?.name) {
      const targetIdx = screens.findIndex(s => s.id === action.next.name);
      if (targetIdx === -1) {
        setCompletionInfo({
          type: 'error',
          title: `Can't find screen "${action.next.name}"`,
          detail: 'The Footer\'s "next.name" doesn\'t match any screen "id" in this flow.',
        });
        return;
      }
      setCompletionInfo(null);
      setScreenIdx(targetIdx);
      return;
    }

    if (action.name === 'complete') {
      setCompletionInfo({
        type: 'complete',
        title: '✅ Flow would complete here',
        detail: 'This is a terminal action — it would send the payload below to your endpoint. Not sent for real during preview.',
        payload: resolvedPayload,
      });
      return;
    }

    if (action.name === 'data_exchange') {
      setCompletionInfo({
        type: 'data_exchange',
        title: '🔌 This screen calls your live endpoint',
        detail: 'data_exchange screens fetch the next screen dynamically from your server — not simulated here to avoid side effects (e.g. a real withdrawal). Use Health Check or the command Test tab to verify this endpoint separately.',
        payload: resolvedPayload,
      });
      return;
    }

    if (action.name === 'open_url') {
      setCompletionInfo({
        type: 'info',
        title: `🔗 Would open a URL`,
        detail: action.url ? String(action.url) : '(no url specified)',
      });
      return;
    }

    // update_data and anything else not specifically handled
    setCompletionInfo({
      type: 'info',
      title: `Action: ${action.name}`,
      detail: 'This action type isn\'t simulated in preview yet.',
      payload: resolvedPayload,
    });
  }

  return (
    <div className="flow-preview-wrap">
      {screens.length > 1 && (
        <div className="flow-preview-screen-nav">
          {screens.map((s, i) => (
            <button key={i} className={`flow-screen-nav-btn ${i === screenIdx ? 'active' : ''}`}
              onClick={() => { setScreenIdx(i); setCompletionInfo(null); }}>
              {s.id ?? `Screen ${i+1}`}
            </button>
          ))}
        </div>
      )}
      {/* Card chrome matches Meta's own WhatsApp Manager Flow preview —
          a small business-avatar circle + close button, NOT a phone
          bezel with a green chat header. Meta's preview never shows the
          screen's own "title" field here; it only ever appears via a
          TextHeading component inside the screen body, if the flow
          author added one. */}
      <div className="flow-phone flow-phone-lg" key={screen?.id}>
        <div className="flow-phone-header">
          <span className="flow-phone-avatar" />
          <span style={{ flex: 1 }} />
          <span className="flow-phone-close">✕</span>
        </div>
        <div className="flow-phone-body">
          <ScreenRenderer screen={screen} onAction={handleAction} />
        </div>
      </div>
      {completionInfo && (
        <div className={`flow-preview-completion flow-preview-completion-${completionInfo.type}`}>
          <div className="flow-preview-completion-title">{completionInfo.title}</div>
          <div className="flow-preview-completion-detail">{completionInfo.detail}</div>
          {completionInfo.payload && (
            <pre className="flow-preview-completion-payload">{JSON.stringify(completionInfo.payload, null, 2)}</pre>
          )}
          <button className="btn btn-outline" style={{ fontSize: 12, marginTop: 8 }} onClick={() => setCompletionInfo(null)}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

// Renders a single screen's fields, tracks answers so the Continue
// button's enabled/disabled state actually reflects whether required
// fields are filled — the same behavior Meta's own preview shows.
function ScreenRenderer({ screen, onAction }) {
  const [answers, setAnswers] = useState({});
  useEffect(() => { setAnswers({}); }, [screen?.id]);

  const dataSchema = screen?.data ?? {};
  const rootChildren = screen?.layout?.children ?? screen?.children ?? [];

  if (!rootChildren.length) return (
    <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>
      No components defined
    </div>
  );

  const footer = findFooter(rootChildren);
  const requiredNames = collectRequiredNames(rootChildren);
  const allRequiredFilled = requiredNames.every(n => {
    const v = answers[n];
    return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  });

  function setAnswer(name, value) {
    setAnswers(a => ({ ...a, [name]: value }));
  }

  return (
    <>
      <div className="flow-preview-fields">
        {rootChildren.map((c, i) => (
          <FlowComponent key={i} comp={c} dataSchema={dataSchema} answers={answers} setAnswer={setAnswer} />
        ))}
      </div>
      <div className="flow-phone-footer">
        <button
          className={`flow-phone-cta ${allRequiredFilled ? 'enabled' : 'disabled'}`}
          disabled={!allRequiredFilled}
          onClick={() => onAction?.(footer?.['on-click-action'], dataSchema, answers)}
        >
          {footer?.label ? resolveText(footer.label, dataSchema) : 'Continue'}
        </button>
        <div className="flow-phone-managed">Managed by the business. <span>Learn more</span></div>
      </div>
    </>
  );
}

// Footer is usually nested inside a Form; walk the tree to find it
// rather than assuming it's a direct child.
function findFooter(children) {
  for (const c of children) {
    const type = (c.type ?? '').toLowerCase();
    if (type === 'footer') return c;
    if (Array.isArray(c.children)) {
      const found = findFooter(c.children);
      if (found) return found;
    }
  }
  return null;
}

// Best-effort: collects the `name` of every component explicitly marked
// required:true, anywhere in the tree. This is an approximation of
// Meta's own validation (which has type-specific defaults we don't try
// to fully replicate here) -- good enough to make Continue's disabled
// state feel real without pretending to be a pixel-perfect validator.
function collectRequiredNames(children) {
  let names = [];
  for (const c of children) {
    if (c.required === true && c.name) names.push(c.name);
    if (Array.isArray(c.children)) names = names.concat(collectRequiredNames(c.children));
  }
  return names;
}

function FlowComponent({ comp, dataSchema, answers, setAnswer }) {
  const type = (comp.type ?? '').toLowerCase();

  if (type === 'form') {
    const inner = comp.children ?? [];
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {inner.map((c, i) => <FlowComponent key={i} comp={c} dataSchema={dataSchema} answers={answers} setAnswer={setAnswer} />)}
    </div>;
  }
  if (type === 'textheading' || type === 'text-heading') {
    return <div className="flow-preview-heading">{resolveText(comp.text, dataSchema)}</div>;
  }
  if (type === 'textsubheading' || type === 'text-subheading') {
    return <div className="flow-preview-subheading">{resolveText(comp.text, dataSchema)}</div>;
  }
  if (type === 'textbody' || type === 'text-body' || type === 'text') {
    return <div className="flow-preview-body-text">{resolveText(comp.text, dataSchema)}</div>;
  }
  if (type === 'textinput' || type === 'text-input') {
    return (
      <div>
        {comp.label && <div className="flow-preview-label">{comp.label}{comp.required && <span className="flow-preview-required">*</span>}</div>}
        <input
          className="flow-preview-input"
          placeholder={comp['helper-text'] ?? ''}
          value={answers[comp.name] ?? ''}
          onChange={e => setAnswer(comp.name, e.target.value)}
        />
      </div>
    );
  }
  if (type === 'textarea') {
    return (
      <div>
        {comp.label && <div className="flow-preview-label">{comp.label}{comp.required && <span className="flow-preview-required">*</span>}</div>}
        <textarea
          className="flow-preview-input"
          style={{ minHeight: 56, resize: 'none' }}
          placeholder={comp['helper-text'] ?? ''}
          value={answers[comp.name] ?? ''}
          onChange={e => setAnswer(comp.name, e.target.value)}
        />
      </div>
    );
  }
  if (type === 'radiobuttons' || type === 'radio-buttons') {
    const opts = asArray(comp['data-source'] ?? comp.options);
    const selected = answers[comp.name];
    return (
      <div>
        {comp.label && <div className="flow-preview-label">{comp.label}{comp.required && <span className="flow-preview-required">*</span>}</div>}
        <div className="flow-preview-option-list">
          {opts.map((o, i) => {
            const val = o.id ?? o.value ?? o.title ?? o.label ?? o;
            const label = o.title ?? o.label ?? String(o);
            return (
              <button key={i} type="button" className="flow-preview-option-row" onClick={() => setAnswer(comp.name, val)}>
                <span>{label}</span>
                <span className={`flow-preview-radio ${selected === val ? 'checked' : ''}`} />
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (type === 'checkboxes' || type === 'checkbox-group') {
    const opts = asArray(comp['data-source'] ?? comp.options);
    const selected = answers[comp.name] ?? [];
    return (
      <div>
        {comp.label && <div className="flow-preview-label">{comp.label}{comp.required && <span className="flow-preview-required">*</span>}</div>}
        <div className="flow-preview-option-list">
          {opts.map((o, i) => {
            const val = o.id ?? o.value ?? o.title ?? o.label ?? o;
            const label = o.title ?? o.label ?? String(o);
            const isChecked = Array.isArray(selected) && selected.includes(val);
            return (
              <button key={i} type="button" className="flow-preview-option-row" onClick={() => {
                setAnswer(comp.name, isChecked ? selected.filter(v => v !== val) : [...selected, val]);
              }}>
                <span>{label}</span>
                <span className={`flow-preview-checkbox ${isChecked ? 'checked' : ''}`} />
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (type === 'dropdown') {
    const opts = asArray(comp['data-source'] ?? comp.options);
    return (
      <div>
        {comp.label && <div className="flow-preview-label">{comp.label}{comp.required && <span className="flow-preview-required">*</span>}</div>}
        <select className="flow-preview-input flow-preview-select" value={answers[comp.name] ?? ''}
          onChange={e => setAnswer(comp.name, e.target.value)}>
          <option value="" disabled>{comp['helper-text'] ?? 'Select…'}</option>
          {opts.map((o, i) => {
            const val = o.id ?? o.value ?? o.title ?? o.label ?? o;
            return <option key={i} value={val}>{o.title ?? o.label ?? String(o)}</option>;
          })}
        </select>
      </div>
    );
  }
  if (type === 'datepicker' || type === 'date-picker') {
    return (
      <div>
        {comp.label && <div className="flow-preview-label">{comp.label}{comp.required && <span className="flow-preview-required">*</span>}</div>}
        <input type="date" className="flow-preview-input" value={answers[comp.name] ?? ''}
          onChange={e => setAnswer(comp.name, e.target.value)} />
      </div>
    );
  }
  if (type === 'image') {
    return (
      <div style={{ background: '#f3f4f6', borderRadius: 8, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 12 }}>
        🖼 Image
      </div>
    );
  }
  if (type === 'footer') return null; // rendered separately by ScreenRenderer, pinned outside the scrollable body
  return (
    <div style={{ fontSize: 11, background: '#f3f4f6', color: '#9ca3af', padding: '4px 8px', borderRadius: 6, fontFamily: 'monospace' }}>
      [{comp.type ?? 'unknown'}]
    </div>
  );
}

// Substitutes ${data.xxx} with that field's __example__ value from the
// screen's own `data` schema -- previously this just stripped the
// wrapper syntax and left the raw key name showing (e.g. a literal
// "msg" instead of the actual example message), which is why the old
// preview never looked like real content. Anything it can't resolve is
// left as the visible ${data.xxx} placeholder, which is more useful
// during editing than silently showing nothing.
function resolveText(val, dataSchema) {
  if (val === undefined || val === null) return '';
  if (typeof val !== 'string') return String(val);
  if (!dataSchema) return val;
  return val.replace(/\$\{data\.([a-zA-Z0-9_]+)\}/g, (match, key) => {
    const field = dataSchema[key];
    if (field && typeof field === 'object' && '__example__' in field) {
      return String(field.__example__);
    }
    return match;
  });
}

// Resolves a Footer action's "payload" object for preview purposes --
// walks it recursively (payloads can nest), substituting both
// ${data.xxx} (the screen's own data schema, via resolveText above) and
// ${form.xxx} (the live value the user just typed/selected in THIS
// screen's form, tracked in ScreenRenderer's `answers` state). Meta
// distinguishes these two namespaces in real Flow JSON; a form field
// reference that never got typed in just shows blank rather than the
// raw placeholder, since "the user didn't fill this in yet" is a more
// honest preview than a literal ${form.x} string.
function resolvePayloadObject(payload, dataSchema, answers) {
  if (payload === null || payload === undefined) return payload;
  if (typeof payload === 'string') {
    const formMatch = payload.match(/^\$\{form\.([a-zA-Z0-9_]+)\}$/);
    if (formMatch) {
      const v = answers?.[formMatch[1]];
      return v === undefined ? '' : v;
    }
    return resolveText(payload, dataSchema);
  }
  if (Array.isArray(payload)) {
    return payload.map(v => resolvePayloadObject(v, dataSchema, answers));
  }
  if (typeof payload === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(payload)) {
      out[k] = resolvePayloadObject(v, dataSchema, answers);
    }
    return out;
  }
  return payload;
}

// ── Flow list card ────────────────────────────────────────────
function FlowCard({ flow, bId, onEdit, onRefresh }) {
  const [busy,    setBusy]    = useState('');
  const [confirm, setConfirm] = useState(null);

  async function doAction(action, label) {
    setBusy(action);
    try {
      const data = await flowsApi(bId, { action, fId: flow.fId });
      if (data.success) { toast.success(`${label} successful`); onRefresh(); }
      else toast.error(data.message ?? `${label} failed`);
    } catch { toast.error('Network error'); }
    setBusy('');
  }

  const updatedDate = flow.fUpdatedAt
    ? new Date(flow.fUpdatedAt.replace(' ', 'T')).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  return (
    <>
      <div className="flow-list-row" onClick={() => onEdit(flow)} title="Click to open editor">
        <div className="flow-list-row-left">
          <div className="flow-list-name">{flow.fName}</div>
          <div className="flow-list-meta">
            <span className="flow-category-tag">{(flow.fCategory ?? 'OTHER').replace(/_/g, ' ')}</span>
            {flow.fMetaFlowId && (
              <span className="flow-meta-id" onClick={e => e.stopPropagation()}>
                ID: {flow.fMetaFlowId}
              </span>
            )}
            {flow.fEndpointUri && (
              <span className="flow-endpoint-tag" title={flow.fEndpointUri}>🔗 Has endpoint</span>
            )}
          </div>
        </div>
        <div className="flow-list-row-right" onClick={e => e.stopPropagation()}>
          <StatusBadge status={flow.fStatus ?? 'draft'} />
          <span className="flow-list-date">{updatedDate}</span>
          {flow.fStatus === 'draft' && flow.fMetaFlowId && (
            <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }}
              onClick={e => { e.stopPropagation(); setConfirm({ action: 'publish', label: 'Publish', title: 'Publish this flow?', message: 'Once published, this flow cannot be edited. Make sure everything looks correct.', variant: 'warning' }); }}
              disabled={!!busy}>
              {busy === 'publish' ? <span className="spinner" style={{ width: 10, height: 10 }} /> : '🚀 Publish'}
            </button>
          )}
          {flow.fStatus === 'published' && (
            <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: 12 }}
              onClick={e => { e.stopPropagation(); setConfirm({ action: 'deprecate', label: 'Deprecate', title: 'Deprecate this flow?', message: 'This flow will no longer be available to users.', variant: 'warning' }); }}
              disabled={!!busy}>
              {busy === 'deprecate' ? <span className="spinner spinner-dark" style={{ width: 10, height: 10 }} /> : 'Deprecate'}
            </button>
          )}
          <button className="icon-btn danger"
            onClick={e => { e.stopPropagation(); setConfirm({ action: 'delete', label: 'Delete', title: 'Delete this flow?', message: 'This will permanently remove the flow from Meta and cannot be undone.', detail: flow.fName, variant: 'danger' }); }}
            disabled={!!busy || flow.fStatus === 'published'}>
            {busy === 'delete' ? <span className="spinner spinner-dark" style={{ width: 10, height: 10 }} /> : '🗑️'}
          </button>
        </div>
      </div>
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          detail={confirm.detail}
          confirmLabel={confirm.label}
          variant={confirm.variant}
          onConfirm={() => doAction(confirm.action, confirm.label)}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ── Main FlowsTab ─────────────────────────────────────────────
export default function FlowsTab({ bId, bot }) {
  const [flows,        setFlows]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [syncing,      setSyncing]      = useState(false);
  const [showCreate,   setShowCreate]   = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [isRental,      setIsRental]      = useState(false);
  const [defaultEndpoint, setDefaultEndpoint] = useState('');
  const [hasKey,        setHasKey]        = useState(null); // null = not checked yet, true/false once known -- HEALTH, not existence
  const [keyExists,     setKeyExists]     = useState(false); // does *something* already exist that generating would overwrite -- see hasKey vs keyExists note in checkKey
  const [keyCheckIssue, setKeyCheckIssue] = useState(''); // set when the check itself failed (distinct from "checked, and there's no key")
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const hasWaba = !!(bot?.bWhatsappId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await flowsGet(bId);
      if (data.success) {
        setFlows(data.flows ?? []);
        setIsRental(!!data.isRental);
        setDefaultEndpoint(data.defaultEndpoint || '');
      } else toast.error(data.message ?? 'Failed to load flows');
    } catch { toast.error('Network error'); }
    setLoading(false);
  }, [bId]);

  // Encryption key is per-bot (tied to the phone number, not the flow), so
  // check it once here rather than only surfacing it deep inside a
  // specific flow's editor -- otherwise a rental customer has no way to
  // even discover this is missing until a health check already failed.
  //
  // This used to only ever set hasKey on a *successful* status check, so
  // if the check itself failed for some other reason (most commonly:
  // bPhoneNumberId isn't set on the bot row -- the same historical
  // createBot() bug that used to drop bWhatsappId/bMetaAppId too), the
  // banner silently never appeared at all. Now any failure still shows a
  // banner, just with whatever reason the backend actually gave.
  const checkKey = useCallback(async () => {
    try {
      const data = await flowsApi(bId, { action: 'get_encryption_status' });
      // eslint-disable-next-line no-console
      console.log('[FlowsTab] get_encryption_status ->', data);
      if (data.success) {
        const healthy = isEncryptionKeyHealthy(data);
        setHasKey(healthy);
        // Deliberately independent of `healthy`: a corrupted-but-present
        // key must still be flagged as "exists" so GenerateKeyModal shows
        // its force-replace warning instead of silently attempting an
        // unforced generate that the backend will reject.
        setKeyExists(!!data.has_key || !!data.local_key_exists);
        // Meta has *a* key on file, but flags it broken -- surface that
        // instead of silently hiding the button (see isEncryptionKeyHealthy).
        setKeyCheckIssue(
          !healthy && data.has_key && UNHEALTHY_KEY_STATUSES.includes(data.key_status)
            ? `Meta has a key on file, but reports its status as "${data.key_status}" — it needs to be replaced.`
            : ''
        );
      } else {
        setHasKey(false);
        setKeyCheckIssue(data.message || 'Could not check encryption status.');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('[FlowsTab] get_encryption_status threw ->', err);
      setHasKey(false);
      setKeyCheckIssue('Could not check encryption status (network error).');
    }
  }, [bId]);

  useEffect(() => { load(); checkKey(); }, [load, checkKey]);

  async function syncFromMeta() {
    if (!hasWaba) return toast.error('Set your WABA ID in Bot Settings first');
    setSyncing(true);
    try {
      const data = await flowsApi(bId, { action: 'sync_from_meta' });
      if (data.success) {
        // On a shared Rent Space WABA, `skipped` counts flows the backend
        // deliberately ignored because they aren't linked to this
        // renter's own bot -- surfacing that here so it doesn't look like
        // sync silently did nothing when other renters have flows on the
        // same account.
        const skipped = data.skipped ?? 0;
        toast.success(
          skipped > 0
            ? `Synced ${data.synced ?? 0} of your flow(s) (${skipped} from other renters skipped)`
            : `Synced ${data.synced ?? 0} flow(s) from Meta`
        );
        load();
      } else {
        toast.error(data.message ?? 'Sync failed');
      }
    } catch { toast.error('Network error'); }
    setSyncing(false);
  }

  const filtered = flows.filter(f => {
    const matchSearch = !search || f.fName.toLowerCase().includes(search.toLowerCase())
      || (f.fMetaFlowId ?? '').includes(search);
    const matchStatus = !filterStatus || f.fStatus === filterStatus;
    return matchSearch && matchStatus;
  });

  if (editing) {
    return (
      <FlowEditor
        flow={editing}
        bId={bId}
        bot={bot}
        isRental={isRental}
        defaultEndpoint={defaultEndpoint}
        onClose={() => { setEditing(null); load(); }}
        onSaved={(updatedFlow) => { if (updatedFlow) setEditing(updatedFlow); load(); }}
      />
    );
  }

  if (loading) return <div className="tab-loading"><span className="spinner spinner-dark" />Loading flows…</div>;

  return (
    <div className="tab-content">
      <div className="tab-toolbar">
        <div>
          <div className="tab-title">WhatsApp Flows</div>
          <div className="tab-sub">{flows.length} flow{flows.length !== 1 ? 's' : ''} · Interactive forms &amp; guided journeys</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={syncFromMeta} disabled={syncing || !hasWaba} title={isRental ? "Refresh flows you've created here — flows other renters created on this shared account are never pulled in" : "Pull existing flows from Meta into your account"}>
            {syncing ? <><span className="spinner spinner-dark" /> Syncing…</> : '🔄 Sync from Meta'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New Flow
          </button>
        </div>
      </div>

      {!hasWaba && (
        <div className="flow-warn-banner" style={{ marginBottom: 20 }}>
          ⚠️ <strong>WABA ID not set.</strong> Go to <strong>Overview → Edit Bot Settings</strong> and add your WhatsApp Business Account ID to create or sync flows.
        </div>
      )}

      {isRental && (
        <div className="flow-warn-banner" style={{ marginBottom: 20 }}>
          ℹ️ This number is on a shared Rent Space WhatsApp account. "Sync from Meta" only ever refreshes flows
          <strong> you've</strong> created here — it never pulls in or shows flows other renters created on this
          same shared account.
        </div>
      )}

      {hasWaba && hasKey === false && (
        <div className="flow-warn-banner" style={{ marginBottom: 20, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          {/^bot has no phone number id/i.test(keyCheckIssue) ? (
            <span>
              ⚠️ <strong>This bot is missing its Phone Number ID</strong> — that has to be fixed on the account
              itself before an encryption key can be set up. Please contact support so we can get this corrected.
            </span>
          ) : (
            <>
              <span>
                ⚠️ <strong>No encryption key set up yet.</strong>{keyCheckIssue ? ` (${keyCheckIssue})` : ''} Any flow
                with data exchange or a health check on this number will fail until this is done — it's a one-time
                step, no technical setup needed on your end.
              </span>
              <button className="btn btn-danger" style={{ fontSize: 12, padding: '6px 14px', whiteSpace: 'nowrap' }}
                onClick={() => setShowGenerateModal(true)}>
                🔑 Set Up Encryption Key
              </button>
            </>
          )}
        </div>
      )}

      {flows.length > 0 && (
        <div className="flow-list-filters">
          <input className="flow-search-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍  Search flows…" />
          <select className="form-input" style={{ width: 140, padding: '8px 12px', fontSize: 13 }}
            value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="deprecated">Deprecated</option>
          </select>
        </div>
      )}

      {flows.length > 0 && (
        <div className="flow-list-header">
          <span>Flow name</span>
          <span>Status</span>
          <span>Last updated</span>
          <span></span>
        </div>
      )}

      {filtered.length === 0 && flows.length === 0 && (
        <div className="tab-empty" style={{ paddingTop: 80 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🌊</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-dark)', marginBottom: 6 }}>No flows yet</div>
          <div style={{ marginBottom: 20, color: 'var(--text-muted)' }}>
            {!hasWaba
              ? 'Set your WABA ID in bot settings first, then create a flow.'
              : isRental
                ? 'Create a new flow, or sync flows you\'ve already created here — flows from other renters on this shared account are never shown.'
                : 'Create a new flow or sync existing flows from your Meta account.'}
          </div>
          {hasWaba && (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn btn-outline" onClick={syncFromMeta} disabled={syncing}>
                {syncing ? 'Syncing…' : '🔄 Sync from Meta'}
              </button>
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Create Flow</button>
            </div>
          )}
        </div>
      )}

      {filtered.length === 0 && flows.length > 0 && (
        <div className="tab-empty">No flows match your search.</div>
      )}

      {filtered.length > 0 && (
        <div className="flow-list">
          {filtered.map(f => (
            <FlowCard key={f.fId} flow={f} bId={bId} onEdit={setEditing} onRefresh={load} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateFlowModal bId={bId} hasWaba={hasWaba} isRental={isRental} defaultEndpoint={defaultEndpoint}
          onClose={() => setShowCreate(false)} onCreated={load} />
      )}

      {showGenerateModal && (
        <GenerateKeyModal bId={bId} hasExistingKey={keyExists}
          onClose={() => setShowGenerateModal(false)}
          onGenerated={() => { setShowGenerateModal(false); checkKey(); }} />
      )}
    </div>
  );
}